/**
 * Represents the interface between the backend and the frontend. Handles the data passing, so that
 * the frontend will only have to interface with these attributes to use the backend.
 */
import {
  RFPEmailStage,
  type RFPEmailTemplateParams,
  sendRFPEmailsAutomatically,
  sendSlackMessage,
  sortVenuesForCustomerView,
} from "@/components/ui/utils";
import { getUserAccessibleEvents } from "@/features/user-access/_logic/database";
import { FormattedLocation } from "@/hooks/useLocationSearch";
import { addVenueToEventVenueCandidate } from "@/lib/database/client/candidate";
import { DraftEvents, Events } from "@/lib/database/events";
import { isTestEvent } from "@/lib/events/test-event";
import { Form, FormField } from "@/lib/database/forms";
import { LandingPage, LandingPageComponent, LandingPageSubpage } from "@/lib/database/landing-pages";
import { EventVenueBooking, EventVenueCandidate, StaticVenue, VenueMeetingSpace } from "@/lib/database/venues";
import { getCurrentUser } from "@/lib/db/users";
import { notifyVenuesNeedManualReview } from "@/lib/notifications/admin-alert-notify-actions";
import { notifyEventStatusChanged } from "@/lib/notifications/admin-status-notify-actions";
import { getVenueOptions, VenueOption } from "@/lib/db/venue_options";
import { getNorthstarPocsSlackMentions } from "@/lib/notifications/slack-mentions";
import { getEventPocSlackMentionAction } from "@/lib/notifications/slack-mentions-actions";
import { adminEventUrl, escapeSlackText } from "@/lib/notifications/slack-text";
import { PartnerType } from "@/lib/constants/venue-partner-types";
import {
  notifyBudgetEstimatorEmail,
  notifyHighValueDraft,
  notifyCustomerReferred,
  notifyNewsletterSignedUp,
  notifyPartnerSignedUp,
  notifyTemplateDownloaded,
  notifyVendorSignedUp,
} from "@/lib/notifications/public-notify-actions";
import { SlugConflictError } from "@/lib/shared/errors";
import {
  EventStatus,
  InternalEventStatus,
  SlackNotificationType,
  StripePaymentStatus,
  WireCategory,
} from "@/lib/shared/enums";
import { Users } from "@/lib/users/types";
import { formatAccommodationsForSlack } from "@/lib/utils/accommodations";
import { formatEventDatesForSlack, getEventDateRange } from "@/lib/utils/event-dates";
import { SupabaseClient } from "@supabase/supabase-js";
import moment from "moment";
import { GamProfile } from "../gam-profiles";
import { hasEventLandingPageSlugConflict, hasLandingPageSlugConflict } from "@/lib/db/landing_pages";
import { getEventOwner } from "./database_admin";

export async function getUserFromId(supabase: SupabaseClient, user_id: number): Promise<Users | null> {
  const { data, error } = await supabase.schema("nowadays").from("users").select("*").eq("user_id", user_id);
  if (error) {
    return null;
  }
  if (data.length != 1) {
    return null;
  }

  return data[0];
}

// returns the org id that the user is the owner of
export async function getOrCreateUserOrgId(supabase: SupabaseClient, user: Users): Promise<number | null> {
  // First try to get existing org id
  const { data: existingOrgId, error: getError } = await supabase
    .schema("nowadays")
    .rpc("get_user_org_id", { _user_id: user.user_id });

  if (getError) {
    console.error("Error getting user org id:", getError);
    return null;
  }

  // If user already has an org, return it
  if (existingOrgId) {
    return existingOrgId as number;
  }

  // Create a new org
  const { data: newOrg, error: orgError } = await supabase
    .schema("nowadays")
    .from("orgs")
    .insert([
      {
        org_name: `${user.full_name || user.email}'s Organization`,
      },
    ])
    .select()
    .single();

  if (orgError) {
    console.error("Error creating new org:", orgError);
    return null;
  }

  // Add user to org_users table as owner
  const { error: userError } = await supabase
    .schema("nowadays")
    .from("org_users")
    .insert([
      {
        org_id: newOrg.org_id,
        user_id: user.user_id,
      },
    ]);

  if (userError) {
    console.error("Error adding user to org:", userError);
    return null;
  }

  return newOrg.org_id;
}

export async function updateOrgName(supabase: SupabaseClient, orgId: number, newName: string): Promise<boolean> {
  try {
    const { error } = await supabase.schema("nowadays").rpc("update_org", {
      p_org_id: orgId,
      p_org_name: newName,
    });

    if (error) {
      console.error("Error updating org name:", error);
      return false;
    }
    return true;
  } catch (error) {
    console.error("Unexpected error updating org name:", error);
    return false;
  }
}

// returns whether the user is a member of an organization
export async function getIsUserMemberOfOrg(
  supabase: SupabaseClient,
  user_id: number,
  org_id: number
): Promise<boolean | null> {
  const { data, error } = await supabase
    .schema("nowadays")
    .from("org_users")
    .select("*")
    .eq("org_id", org_id)
    .eq("user_id", user_id);
  if (error) {
    return null;
  }

  return data.length > 0;
}

// Enterprise org configuration is stored in the enterprise_themes table.
// Use getEnterpriseOrgBySlug(slug) from @/lib/database/client/enterprise-org.ts

/**
 * Add a user to an organization if they're not already a member.
 * Returns the org_id on success.
 */
export async function addUserToOrg(supabase: SupabaseClient, userId: number, orgId: number): Promise<number> {
  const isMember = await getIsUserMemberOfOrg(supabase, userId, orgId);

  if (isMember) {
    return orgId;
  }

  const { error } = await supabase.schema("nowadays").from("org_users").insert({
    org_id: orgId,
    user_id: userId,
  });

  if (error) {
    console.error("Error adding user to org:", error);
    throw error;
  }

  return orgId;
}

/**
 * Remove a user from an organization.
 * @param supabase - The Supabase client
 * @param userId - The user ID to remove
 * @param orgId - The organization ID
 */
export async function removeUserFromOrg(supabase: SupabaseClient, userId: number, orgId: number): Promise<void> {
  const { error } = await supabase
    .schema("nowadays")
    .from("org_users")
    .delete()
    .eq("user_id", userId)
    .eq("org_id", orgId);

  if (error) {
    console.error("Error removing user from org:", error);
    throw error;
  }
}

/**
 * Get all organizations a user is a member of
 */
export type OrgData = {
  org_id: number;
  org_name: string;
  industry?: string;
  hq_city?: string;
  hq_address?: string;
};

/**
 * Fetch a single organization by ID
 */
export async function getOrganizationById(supabase: SupabaseClient, orgId: number): Promise<OrgData | null> {
  const { data: org, error } = await supabase
    .schema("nowadays")
    .from("orgs")
    .select("org_id, org_name, industry, hq_city, hq_address")
    .eq("org_id", orgId)
    .single();

  if (error || !org) {
    console.error("Error fetching organization:", error);
    return null;
  }

  return org;
}

export async function getUserOrganizations(supabase: SupabaseClient, userId: number): Promise<OrgData[]> {
  // First get the org IDs for the user
  const { data: orgUsers, error: orgUsersError } = await supabase
    .schema("nowadays")
    .from("org_users")
    .select("org_id")
    .eq("user_id", userId);

  if (orgUsersError || !orgUsers) {
    console.error("Error fetching user org associations:", orgUsersError);
    return [];
  }

  const orgIds = orgUsers.map((ou) => ou.org_id);

  if (orgIds.length === 0) {
    return [];
  }

  // Then get the org details
  const { data: orgs, error: orgsError } = await supabase
    .schema("nowadays")
    .from("orgs")
    .select("*")
    .in("org_id", orgIds);

  if (orgsError) {
    console.error("Error fetching organizations:", orgsError);
    return [];
  }

  return orgs || [];
}

/**
 * Get all members (users) of an organization.
 * Uses a single query with join for better performance.
 * @param supabase - The Supabase client
 * @param orgId - The organization ID
 * @returns Array of users who are members of the organization
 */
export async function getOrgMembers(supabase: SupabaseClient, orgId: number): Promise<Users[]> {
  const { data, error } = await supabase
    .schema("nowadays")
    .from("org_users")
    .select(
      `
      users:user_id (*)
    `
    )
    .eq("org_id", orgId);

  if (error) {
    console.error("Error fetching org members:", error);
    return [];
  }

  // Extract users from the nested structure and filter out any null results
  const users = data?.map((row) => row.users as unknown as Users).filter((user): user is Users => user !== null) || [];
  return users;
}

/**
 * Update organization details
 */
export async function updateOrganization(
  supabase: SupabaseClient,
  orgId: number,
  updates: {
    org_name?: string;
    industry?: string;
    hq_city?: string;
    hq_address?: string;
    logo_url?: string;
    event_preferences?: any;
    contract_clauses?: any;
  }
): Promise<boolean> {
  const { data, error } = await supabase.schema("nowadays").from("orgs").update(updates).eq("org_id", orgId).select();

  if (error) {
    console.error("Error updating organization:", error);
    console.error("Update payload:", updates);
    return false;
  }

  return true;
}

/**
 * Check if an organization has complete details
 */
export async function isOrgDetailsComplete(supabase: SupabaseClient, orgId: number): Promise<boolean> {
  const { data, error } = await supabase
    .schema("nowadays")
    .from("orgs")
    .select("org_name, industry, hq_city, hq_address")
    .eq("org_id", orgId)
    .single();

  if (error || !data) {
    console.error("Error checking org details:", error);
    return false;
  }

  // Check if all important fields are filled
  return !!(data.org_name && data.industry && data.hq_city);
}

/**
 * Retrieves the user type for a given user and event.
 *
 * @param supabase The SupabaseClient instance.
 * @param user_id The ID of the user.
 * @param event_id The ID of the event.
 * @returns The user type as a string or null if not found.
 */
export async function getAccessType(
  supabase: SupabaseClient,
  user_id: number,
  event_id: number
): Promise<string | null> {
  const { data, error } = await supabase
    .schema("nowadays")
    .from("events_with_user_owner_id")
    .select("user_type")
    .eq("user_id", user_id)
    .eq("event_id", event_id)
    .limit(1) // Ensure only one row is returned
    .maybeSingle(); // Use maybeSingle to handle cases with 0 rows

  if (error) {
    console.error("Error fetching user type:", error);
    return null;
  }

  return data?.user_type || null;
}

/**
 * Gets all events for a user.
 *
 * @param supabase
 * @param user
 * @returns
 */
export async function getUserEvents(supabase: SupabaseClient, user: Users): Promise<Events[]> {
  // First get user's event access
  const { data: accessData, error: accessError } = await getUserAccessibleEvents(supabase, user.user_id);

  if (accessError || !accessData || accessData.length === 0) {
    return [];
  }

  // Get the event IDs
  const eventIds = accessData.map((a) => a.event_id);

  // Now fetch the full events with all fields including event_dates
  const { data, error } = await supabase.schema("nowadays").from("events").select("*").in("event_id", eventIds);

  if (error) {
    return [];
  }

  return data || [];
}

export async function getUserEventsWithCoverPhoto(
  supabase: SupabaseClient,
  user: Users
): Promise<(Events & { cover_photo: string })[]> {
  const events = await getUserEvents(supabase, user);

  const eventsWithCoverPhotos = await Promise.all(
    events.map(async (event) => {
      const options = await getVenueOptions(supabase, event.event_id);
      if (options) {
        const filteredOptions = options.filter(
          (option: VenueOption) =>
            option.candidate.show_on_dashboard &&
            option.venue?.photos_manually_verified && // Add null check here
            !option.candidate.archived &&
            !option.candidate.customer_removed
        );

        const sortedOptions = sortVenuesForCustomerView(filteredOptions, null);
        const topOption = sortedOptions[0];

        if (topOption) {
          return { ...event, cover_photo: topOption.venue.cover_photo_url ?? "" };
        }
      }

      return { ...event, cover_photo: "/tech-event.png" }; // Default image
    })
  );

  return eventsWithCoverPhotos;
}

export async function getMeetingSpaces(
  supabase: SupabaseClient,
  venue_static_id: number
): Promise<{ data: VenueMeetingSpace[]; error: any }> {
  // First, try the new detailed table
  const { data: detailedData, error: detailedError } = await supabase
    .schema("nowadays")
    .from("venue_static_meeting_space_detailed")
    .select("*")
    .eq("venue_static_id", venue_static_id);

  if (detailedData && detailedData.length > 0) {
    // Map the detailed data to VenueMeetingSpace format
    const mappedData = detailedData.map((space) => ({
      ...space,
      dimensions:
        space.width && space.length ? `${space.width} × ${space.length} ${space.dimension_unit || "ft"}` : undefined,
    }));
    return { data: mappedData, error: detailedError };
  }

  // Fallback to old table
  const { data: oldData, error: oldError } = await supabase
    .schema("nowadays")
    .from("venue_static_meeting_space")
    .select("*")
    .eq("venue_static_id", venue_static_id);

  if (oldData && oldData.length > 0) {
    return { data: oldData, error: oldError };
  }

  // If both tables are empty, return empty array
  return { data: [], error: null };
}

/**
 * Get images for a specific meeting space from storage
 */
export async function getMeetingSpaceImages(
  supabase: SupabaseClient,
  venue_static_id: number,
  meeting_space_id: number
): Promise<{ url: string; filename: string; type: string }[]> {
  try {
    const storagePath = `${venue_static_id}/meeting_rooms_information/${meeting_space_id}/`;
    const { data: files, error } = await supabase.storage.from("venue_photos").list(storagePath);

    if (error || !files || files.length === 0) {
      return [];
    }

    const images = files
      .filter((file) => {
        // Filter out hidden files and placeholders
        if (file.name.startsWith(".") || file.name.includes(".emptyFolderPlaceholder")) {
          return false;
        }
        // Only include image and PDF files
        const ext = file.name.split(".").pop()?.toLowerCase();
        return ["png", "jpg", "jpeg", "gif", "webp", "pdf"].includes(ext || "");
      })
      .map((file) => {
        const { data } = supabase.storage.from("venue_photos").getPublicUrl(`${storagePath}${file.name}`);

        const ext = file.name.split(".").pop()?.toLowerCase() || "";
        const type = ext === "pdf" ? "pdf" : "image";

        return {
          url: data.publicUrl,
          filename: file.name,
          type,
        };
      });

    return images;
  } catch (error) {
    console.error("Error fetching meeting space images:", error);
    return [];
  }
}

/**
 * Get hotel resources files from storage
 */
export async function getHotelResources(
  supabase: SupabaseClient,
  venue_static_id: number
): Promise<{ url: string; filename: string; originalName: string; type: string }[]> {
  try {
    const storagePath = `${venue_static_id}/hotel_resources/`;
    const { data: files, error } = await supabase.storage.from("venue_photos").list(storagePath);

    if (error || !files || files.length === 0) {
      return [];
    }

    const resources = files
      .filter((file) => {
        // Filter out hidden files and placeholders
        if (file.name.startsWith(".") || file.name.includes(".emptyFolderPlaceholder")) {
          return false;
        }
        return true;
      })
      .map((file) => {
        const { data } = supabase.storage.from("venue_photos").getPublicUrl(`${storagePath}${file.name}`);

        const ext = file.name.split(".").pop()?.toLowerCase() || "";
        const type = ext === "pdf" ? "pdf" : ext === "png" || ext === "jpg" || ext === "jpeg" ? "image" : "file";

        // Extract original name from filename (sanitized name might have underscores)
        // For now, use filename as display name
        const originalName = file.name.replace(/_/g, " ").replace(/\.[^/.]+$/, "");

        return {
          url: data.publicUrl,
          filename: file.name,
          originalName,
          type,
        };
      });

    return resources;
  } catch (error) {
    console.error("Error fetching hotel resources:", error);
    return [];
  }
}

/**
 * Get floor plans files from storage
 */
export async function getFloorPlans(
  supabase: SupabaseClient,
  venue_static_id: number
): Promise<{ url: string; filename: string; originalName: string; type: string }[]> {
  try {
    const storagePath = `${venue_static_id}/floor_plans/`;
    const { data: files, error } = await supabase.storage.from("venue_photos").list(storagePath);

    if (error || !files || files.length === 0) {
      return [];
    }

    const floorPlans = files
      .filter((file) => {
        // Filter out hidden files and placeholders
        if (file.name.startsWith(".") || file.name.includes(".emptyFolderPlaceholder")) {
          return false;
        }
        return true;
      })
      .map((file) => {
        const { data } = supabase.storage.from("venue_photos").getPublicUrl(`${storagePath}${file.name}`);

        const ext = file.name.split(".").pop()?.toLowerCase() || "";
        const type = ext === "pdf" ? "pdf" : ext === "png" || ext === "jpg" || ext === "jpeg" ? "image" : "file";

        // Extract original name from filename
        const originalName = file.name.replace(/_/g, " ").replace(/\.[^/.]+$/, "");

        return {
          url: data.publicUrl,
          filename: file.name,
          originalName,
          type,
        };
      });

    return floorPlans;
  } catch (error) {
    console.error("Error fetching floor plans:", error);
    return [];
  }
}

/**
 * Get signed URL for a user's profile picture.
 */

export async function getProfilePictureSignedUrl(supabase: SupabaseClient, user: Users): Promise<string> {
  // signed url, expires in 100 years
  const DEFAULT_PROFILE_URL =
    "https://vqftsjfwhonwbpyogmzi.supabase.co/storage/v1/object/sign/profile_pictures/default.png?token=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1cmwiOiJwcm9maWxlX3BpY3R1cmVzL2RlZmF1bHQucG5nIiwiaWF0IjoxNzE1OTcyMTU1LCJleHAiOjQ4Njk1NzIxNTV9.vgRL12XX-rJEbOMlV7qTlhps0metQ6emKXCPX4HjgSY&t=2024-05-17T18%3A55%3A55.753Z";

  if (!user || !user.profile_picture_url) {
    const session = await supabase.auth.getUser();
    if (session && session.data.user) {
      return session.data.user.user_metadata.avatar_url;
    } else {
      return DEFAULT_PROFILE_URL;
    }
  }

  // If the URL is already a full public URL, return it directly
  if (user.profile_picture_url.startsWith("http")) {
    return user.profile_picture_url;
  }

  // For backwards compatibility with old data that only has the path
  const { data, error } = await supabase.storage
    .from("profile_pictures")
    .createSignedUrl(user.profile_picture_url, 60 * 60 * 24 * 365); // URL will be valid for 1 year

  if (error) {
    const session = await supabase.auth.getUser();
    if (session && session.data.user) {
      return session.data.user.user_metadata.avatar_url;
    } else {
      return DEFAULT_PROFILE_URL;
    }
  }

  return data.signedUrl;
}

/**
 * Get image URLs and captions for a venue.
 * Reads from the image_urls column on venue_static. Falls back to storage listing if image_urls is empty (deprecated).
 *
 * @param supabase - The Supabase client
 * @param venue_static_id - The ID of the venue
 * @returns Array of image data objects, or null on error
 */
export async function getVenueImagesAndCaptions(
  supabase: SupabaseClient,
  venue_static_id: number
): Promise<{ url: string; caption?: string; filename: string }[] | null> {
  try {
    const { data, error } = await supabase
      .schema("nowadays")
      .from("venue_static")
      .select("image_urls")
      .eq("venue_static_id", venue_static_id)
      .maybeSingle();

    if (error) {
      console.error("Error fetching image_urls for venue:", error);
      return null;
    }

    if (data?.image_urls && data.image_urls.length > 0) {
      return data.image_urls.map((url: string) => ({
        url,
        filename: url.split("/").pop() || "",
      }));
    }

    // Deprecated fallback: list images from storage for venues without image_urls populated
    console.warn(`[DEPRECATED] Falling back to storage listing for venue ${venue_static_id} — image_urls is empty`);
    return await getVenueImagesFromStorage(supabase, venue_static_id);
  } catch (err) {
    console.error("Error fetching images for venue %s:", venue_static_id, err);
    return null;
  }
}

/**
 * @deprecated Use image_urls column on venue_static instead. This function lists images from Supabase storage.
 *
 * @param supabase - The Supabase client
 * @param venue_static_id - The ID of the venue
 * @returns Array of image data objects, or empty array
 */
async function getVenueImagesFromStorage(
  supabase: SupabaseClient,
  venue_static_id: number
): Promise<{ url: string; caption?: string; filename: string }[]> {
  const timeoutPromise = new Promise<never>((_, reject) => {
    setTimeout(() => reject(new Error("Request timeout")), 10000);
  });

  const { data: files, error } = await Promise.race([
    supabase.storage.from("venue_photos").list(`${venue_static_id}/`),
    timeoutPromise,
  ]);

  if (error || !files || files.length === 0) {
    return [];
  }

  const excludedFolders = ["suggested_photos", "dining", "meeting_rooms"];

  return files
    .filter((file) => {
      if (!file.metadata || file.metadata.size === undefined) return false;
      if (excludedFolders.includes(file.name)) return false;
      if (file.name.includes("/")) return false;
      if (file.name.startsWith(".") || file.name.includes(".emptyFolderPlaceholder")) return false;
      return true;
    })
    .map((file) => {
      const {
        data: { publicUrl },
      } = supabase.storage.from("venue_photos").getPublicUrl(`${venue_static_id}/${file.name}`);
      return { url: publicUrl, filename: file.name };
    })
    .filter((item) => item.url && item.filename);
}

export async function getEventPayments(supabase: SupabaseClient, eventId: number): Promise<any[]> {
  const { data, error } = await supabase
    .schema("nowadays")
    .from("payments")
    .select("*")
    .eq("event_id", eventId)
    .or("payment_label.neq.TEST,payment_label.is.null") // Exclude 'TEST' but include NULL labels
    .eq("payment_status", StripePaymentStatus.SUCCEEDED);

  if (error) {
    console.error("Error fetching event payments:", error);
    throw new Error("Failed to fetch event payments");
  }

  return data;
}

export async function archivePayment(supabase: SupabaseClient, paymentId: number): Promise<void> {
  const { error } = await supabase
    .schema("nowadays")
    .from("payments")
    .update({ payment_status: StripePaymentStatus.ARCHIVED }) // Set status to 'archived'
    .eq("id", paymentId);

  if (error) {
    console.error("Error archiving payment:", error);
    throw new Error("Failed to archive payment");
  }

  const user = await getCurrentUser(supabase);

  await sendSlackMessage(
    SlackNotificationType.PAYMENTS,
    "PRODUCT ARCHIVED",
    `Payment records have been updated to 'archived' for payment ID ${paymentId}`,
    [
      { title: "Product ID", message: paymentId.toString() },
      { title: "Status", message: "Archived" },
      {
        title: "User that archived",
        message: user?.first_name && user?.last_name ? `${user.first_name} ${user.last_name}` : "Unknown",
      },
    ]
  );
}

export async function getPendingEventPayments(supabase: SupabaseClient, eventId: number): Promise<any[]> {
  const { data, error } = await supabase
    .schema("nowadays")
    .from("payments")
    .select("*")
    .eq("event_id", eventId)
    .or("payment_label.neq.TEST,payment_label.is.null") // Exclude 'TEST' but include NULL labels
    .in("payment_status", [
      StripePaymentStatus.PENDING,
      StripePaymentStatus.SAVE_FOR_LATER,
      StripePaymentStatus.SCHEDULED,
    ]); // Include pending, save_for_later, and scheduled statuses

  if (error) {
    console.error("Error fetching pending event payments:", error);
    throw new Error("Failed to fetch pending event payments");
  }

  return data;
}

export async function getEventWireTransfers(
  supabase: SupabaseClient,
  eventId: number,
  isAdmin: boolean = false
): Promise<any[]> {
  let query = supabase.schema("nowadays").from("payments_brex_wires").select("*").eq("event_id", eventId);

  // For regular users, only show Customer category
  if (!isAdmin) {
    query = query.eq("category", WireCategory.Customer);
  } else {
    // For admins, show both Customer and Vendor categories
    query = query.in("category", [WireCategory.Customer, WireCategory.Vendor, WireCategory.CustomerVendor]);
  }

  const { data, error } = await query.order("date", { ascending: false });

  if (error) {
    console.error("Error fetching event wire transfers:", error);
    throw new Error("Failed to fetch event wire transfers");
  }

  return data || [];
}

export async function updatePaymentMethod(
  supabase: SupabaseClient,
  params: {
    eventId?: number;
    stripeCustomerId: string;
    lastFour: string;
    cardBrand: string;
    paymentMethodId: string; // Renamed from setupIntentId
    userId: number;
  }
): Promise<void> {
  const { eventId, stripeCustomerId, lastFour, cardBrand, paymentMethodId, userId } = params; // Renamed

  // Check for existing card for this user
  const { data: existingCard } = await supabase
    .schema("nowadays")
    .from("payment_methods")
    .select("*")
    .eq("stripe_customer_id", stripeCustomerId)
    .eq("last_four", lastFour)
    .eq("card_brand", cardBrand)
    .eq("user_id", userId)
    .maybeSingle();

  if (existingCard) {
    // Update existing card
    await supabase
      .schema("nowadays")
      .from("payment_methods")
      .update({
        updated_at: new Date().toISOString(),
        payment_method_id: paymentMethodId,
      })
      .eq("id", existingCard.id);

    // Update event with the existing card's database record ID
    if (eventId) {
      await supabase
        .schema("nowadays")
        .from("events")
        .update({ default_payment_method_id: existingCard.id })
        .eq("event_id", eventId);
    }
  } else {
    // Insert new card
    const { data: newCard, error: insertError } = await supabase
      .schema("nowadays")
      .from("payment_methods")
      .insert({
        user_id: userId,
        stripe_customer_id: stripeCustomerId,
        last_four: lastFour,
        card_brand: cardBrand,
        payment_method_id: paymentMethodId,
      })
      .select()
      .single();

    if (insertError) {
      console.error("Error inserting new card:", insertError);
      throw insertError;
    }

    // Update event with the database record ID, not the Stripe payment method ID
    if (eventId && newCard) {
      await supabase
        .schema("nowadays")
        .from("events")
        .update({ default_payment_method_id: newCard.id })
        .eq("event_id", eventId);
    }
  }

  const user = await getUserFromId(supabase, userId);

  if (eventId) {
    await sendSlackMessage(
      SlackNotificationType.PAYMENTS,
      "NEW PAYMENT METHOD",
      `New payment method added for event ${eventId}`,
      [
        { title: "Event ID", message: eventId.toString() },
        {
          title: "User ID",
          message: user?.first_name && user?.last_name ? `${user.first_name} ${user.last_name}` : "Unknown",
        },
        { title: "Stripe Customer ID", message: stripeCustomerId },
        { title: "Last Four", message: lastFour },
        { title: "Card Brand", message: cardBrand },
        { title: "Payment Method ID", message: paymentMethodId },
      ]
    );
  }
}

/**
 * USER MANAGEMENT - EDIT FUNCTIONS
 *
 * these operations will change the database, but only one field at a time
 *
 */
export async function editUserName(
  supabase: SupabaseClient,
  user: Users,
  first_name?: string,
  last_name?: string
): Promise<void> {
  const userData: Partial<Users> = {
    first_name: first_name ?? user.first_name,
    last_name: last_name ?? user.last_name,
  };

  const { error } = await supabase.schema("nowadays").from("users").update(userData).eq("user_id", user.user_id);
  if (error) {
    console.error("Error updating user:", JSON.stringify(error, null, 2));
    throw error;
  }
}

export async function editUserCompany(supabase: SupabaseClient, user: Users, company?: string): Promise<void> {
  const userData: Partial<Users> = {
    company: company ?? user.company,
  };

  const { error } = await supabase.schema("nowadays").from("users").update(userData).eq("user_id", user.user_id);
  if (error) {
    console.error("Error updating user:", JSON.stringify(error, null, 2));
    throw error;
  }
}

export async function editUserNumEventOrganized(
  supabase: SupabaseClient,
  user: Users,
  number_of_events_organized?: number
): Promise<void> {
  const userData: Partial<Users> = {
    number_of_events_organized: number_of_events_organized ?? user.number_of_events_organized,
  };

  const { error } = await supabase.schema("nowadays").from("users").update(userData).eq("user_id", user.user_id);
  if (error) {
    console.error("Error updating user:", JSON.stringify(error, null, 2));
    throw error;
  }
}

export async function editUserTitle(supabase: SupabaseClient, user: Users, title?: string): Promise<void> {
  const userData: Partial<Users> = {
    title: title ?? user.title,
  };

  const { error } = await supabase.schema("nowadays").from("users").update(userData).eq("user_id", user.user_id);
  if (error) {
    console.error("Error updating user:", JSON.stringify(error, null, 2));
    throw error;
  }
}

export async function editUserAnnualEventBudget(
  supabase: SupabaseClient,
  user: Users,
  annual_event_budget?: number
): Promise<void> {
  const userData: Partial<Users> = {
    annual_event_budget: annual_event_budget ?? user.annual_event_budget,
  };

  const { error } = await supabase.schema("nowadays").from("users").update(userData).eq("user_id", user.user_id);
  if (error) {
    console.error("Error updating user:", JSON.stringify(error, null, 2));
    throw error;
  }
}

export async function editTypesOfEvents(
  supabase: SupabaseClient,
  user: Users,
  types_of_events?: string[]
): Promise<void> {
  const { error } = await supabase
    .schema("nowadays")
    .from("users")
    .update({
      types_of_events: types_of_events ?? user.types_of_events,
    })
    .eq("user_id", user.user_id);
  if (error) {
    console.error("Error updating user:", JSON.stringify(error, null, 2));
    throw error;
  }
}

export async function editIndustry(supabase: SupabaseClient, user: Users, industry?: string): Promise<void> {
  const { error } = await supabase
    .schema("nowadays")
    .from("users")
    .update({
      industry: industry ?? user.industry,
    })
    .eq("user_id", user.user_id);
  if (error) {
    console.error("Error updating user industry:", JSON.stringify(error, null, 2));
    throw error;
  }
}

export async function editHeadquartersCity(
  supabase: SupabaseClient,
  user: Users,
  headquarters_city?: string
): Promise<void> {
  const { error } = await supabase
    .schema("nowadays")
    .from("users")
    .update({
      headquarters_city: headquarters_city ?? user.headquarters_city,
    })
    .eq("user_id", user.user_id);
  if (error) {
    console.error("Error updating user headquarters city:", JSON.stringify(error, null, 2));
    throw error;
  }
}

export async function editPhoneNumber(
  supabase: SupabaseClient,
  user: Users,
  phone_number?: string,
  country_dial_code_id?: number | null
): Promise<void> {
  const updateData: { phone?: string; country_dial_code_id?: number | null } = {};

  if (phone_number !== undefined) {
    updateData.phone = phone_number;
  }

  if (country_dial_code_id !== undefined) {
    updateData.country_dial_code_id = country_dial_code_id;
  }

  const { error } = await supabase.schema("nowadays").from("users").update(updateData).eq("user_id", user.user_id);
  if (error) {
    console.error("Error updating user phone number:", JSON.stringify(error, null, 2));
    throw error;
  }
}

export async function editIsNorthstarUser(
  supabase: SupabaseClient,
  user: Users,
  is_northstar_user?: boolean
): Promise<void> {
  const { error } = await supabase
    .schema("nowadays")
    .from("users")
    .update({
      is_northstar_user: is_northstar_user ?? user.is_northstar_user,
    })
    .eq("user_id", user.user_id);
  if (error) {
    console.error("Error updating user:", JSON.stringify(error, null, 2));
    throw error;
  }
}

/**
 * Updates the commission details for an event.
 *
 * @param supabase The SupabaseClient instance.
 * @param eventId The ID of the event to update.
 * @param commissionDetails An object containing the commission details to update.
 */
export async function updateEventCommissionDetails(
  supabase: SupabaseClient,
  eventId: number,
  commissionDetails: {
    expected_commission?: number | null;
    received_commission?: number | null;
    date_expected_commission?: string | null;
    date_received_commission?: string | null;
  }
): Promise<Events | null> {
  const payload: any = {
    ...(commissionDetails.expected_commission !== undefined
      ? { expected_commission: commissionDetails.expected_commission }
      : {}),
    ...(commissionDetails.received_commission !== undefined
      ? { received_commission: commissionDetails.received_commission }
      : {}),
    // Set to null if undefined, otherwise process the date
    date_expected_commission: commissionDetails.date_expected_commission ?? null,
    date_received_commission: commissionDetails.date_received_commission ?? null,
  };

  const { data, error } = await supabase
    .schema("nowadays")
    .from("events")
    .update(payload)
    .eq("event_id", eventId)
    .select();

  if (error) {
    console.error("Error updating event commission details:", JSON.stringify(error, null, 2));
    throw error;
  }
  if (data) {
    return data[0] ?? null;
  } else {
    return null;
  }
}

const ALLOWED_IMAGE_EXTENSIONS = new Set(["jpg", "jpeg", "png"]);
const IMAGE_CONTENT_TYPES: Record<string, string> = { jpg: "image/jpeg", jpeg: "image/jpeg", png: "image/png" };

/**
 * Uploads a profile picture for a user.
 * Uses the user's UUID as the storage filename with a cache-busting timestamp.
 * Falls back to auth UUID if the uuid column migration hasn't run yet.
 */
export async function uploadProfilePicture(supabase: SupabaseClient, user: Users, picture: File): Promise<void> {
  const rawExtension = picture.name.split(".").pop()?.toLowerCase() ?? "";
  if (!ALLOWED_IMAGE_EXTENSIONS.has(rawExtension)) {
    throw new Error("Invalid file type. Only JPG, JPEG, and PNG are allowed.");
  }

  let filePath: string;
  if (user.uuid) {
    filePath = `${user.uuid}.${rawExtension}`;
  } else {
    const userSession = await supabase.auth.getUser();
    if (!userSession.data.user) {
      throw new Error("Cannot upload profile picture: no authenticated user");
    }
    filePath = `${userSession.data.user.id}/profile.${rawExtension}`;
  }

  const { error: uploadError } = await supabase.storage.from("profile_pictures").upload(filePath, picture, {
    cacheControl: "0",
    upsert: true,
    contentType: IMAGE_CONTENT_TYPES[rawExtension],
  });

  if (uploadError) {
    console.error("Error uploading profile picture:", JSON.stringify(uploadError, null, 2));
    throw uploadError;
  }

  const baseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL;
  const cacheBuster = Date.now();

  const { error: updateError } = await supabase
    .schema("nowadays")
    .from("users")
    .update({
      profile_picture_url: `${baseUrl}/storage/v1/object/public/profile_pictures/${filePath}?t=${cacheBuster}`,
    })
    .eq("user_id", user.user_id);

  if (updateError) {
    console.error("Error updating user profile picture:", JSON.stringify(updateError, null, 2));
    throw updateError;
  }
}

/**
 * Removes a user's profile picture from storage and clears the URL in the database.
 */
export async function removeProfilePicture(supabase: SupabaseClient, user: Users): Promise<void> {
  if (user.profile_picture_url) {
    const urlPath = user.profile_picture_url.split("/profile_pictures/")[1]?.split("?")[0];
    if (urlPath) {
      await supabase.storage.from("profile_pictures").remove([urlPath]);
    }
  }

  const { error } = await supabase
    .schema("nowadays")
    .from("users")
    .update({ profile_picture_url: null })
    .eq("user_id", user.user_id);

  if (error) {
    console.error("Error removing profile picture:", JSON.stringify(error, null, 2));
    throw error;
  }
}

/**
 * EVENT MANAGEMENT - GET OPERATIONS
 *
 * these operations will not modify the database. instead, it will return existing event data.
 *
 * note that this will fail if the user does not have access to the event
 *
 */
export async function getEvent(supabase: SupabaseClient, event_id: number): Promise<Events | null> {
  const { data, error } = await supabase.schema("nowadays").from("events").select("*").eq("event_id", event_id);
  if (error) {
    return null;
  }
  if (data.length != 1) {
    return null;
  }

  return data[0];
}

/**
 * EVENT MANAGEMENT - GET OPERATIONS
 *
 * these operations will not modify the database. instead, it will return existing event data.
 *
 */
export async function getMostRecentEventFromUser(supabase: SupabaseClient, user: Users): Promise<Events | null> {
  const { data, error } = await supabase
    .schema("nowadays")
    .from("events_with_user_owner_id")
    .select("*")
    .eq("user_id", user.user_id)
    .order("created_at", { ascending: false, nullsFirst: false });
  if (error) {
    return null;
  }
  if (data.length === 0) {
    return null;
  }

  return data[0];
}

/**
 * Helper function to fetch chain and brand names for venues and create maps.
 */
export async function fetchChainAndBrandMaps(
  supabase: SupabaseClient,
  venues: Array<{ venue?: { chain_id?: number | null; brand_id?: number | null } | null }>
): Promise<{ chainMap: Map<number, string>; brandMap: Map<number, string> }> {
  const chainIds = [...new Set(venues.map((v) => v.venue?.chain_id).filter(Boolean))];
  const brandIds = [...new Set(venues.map((v) => v.venue?.brand_id).filter(Boolean))];

  const [chains, brands] = await Promise.all([
    chainIds.length
      ? supabase.schema("nowadays").from("venue_chains").select("chain_id, chain_name").in("chain_id", chainIds)
      : { data: [] },
    brandIds.length
      ? supabase.schema("nowadays").from("venue_chain_brands").select("brand_id, brand_name").in("brand_id", brandIds)
      : { data: [] },
  ]);

  const chainMap = new Map((chains.data || []).map((c) => [c.chain_id, c.chain_name]));
  const brandMap = new Map((brands.data || []).map((b) => [b.brand_id, b.brand_name]));

  return { chainMap, brandMap };
}

/**
 * Helper function to enrich venue with chain_name and brand_name from maps.
 */
export function enrichVenueWithChainAndBrand(
  venue: StaticVenue | null | undefined,
  chainMap: Map<number, string>,
  brandMap: Map<number, string>
): StaticVenue | null {
  if (!venue) return null;

  return {
    ...venue,
    chain_name: venue.chain_id ? chainMap.get(venue.chain_id) || undefined : undefined,
    brand_name: venue.brand_id ? brandMap.get(venue.brand_id) || undefined : undefined,
  };
}

/**
 * Returns the data required to display on the table for the admin dashboard, or other purposes
 */
export async function loadVenueDataForEventAndHotel(
  supabase: SupabaseClient,
  event: Events,
  hotel: StaticVenue
): Promise<
  | {
      candidate: EventVenueCandidate;
      hotel: StaticVenue;
      oeeQuote: any | null;
      hasValidQuote: boolean;
    }[]
  | null
> {
  const { data, error } = await supabase
    .schema("nowadays")
    .from("venue_options")
    .select(`*`)
    .eq("event_id", event.event_id)
    .eq("venue_static_id", hotel.venue_static_id);

  if (error) {
    return null;
  }

  const { chainMap, brandMap } = await fetchChainAndBrandMaps(supabase, data);

  return data.map((values) => {
    const oeeQuote =
      (values as any).oee_quote || (values as any).estimated_oee_quote || (values as any).negotiated_oee_quote || null;
    const hasValidQuote = oeeQuote && oeeQuote.grand_total !== null && oeeQuote.grand_total !== undefined;

    return {
      candidate: values.candidate,
      hotel: enrichVenueWithChainAndBrand(values.venue, chainMap, brandMap) || values.venue!,
      oeeQuote: oeeQuote || null,
      hasValidQuote: !!hasValidQuote,
    };
  });
}

/**
 * Returns venue options, where candidate is the user inputted comments for a hotel,
 * and the hotel is the hotel in reference. Returns null if an error occurred.
 *
 * @param supabase
 * @param event
 * @returns
 */
export async function getEventVenueOptions(
  supabase: SupabaseClient,
  event: Events
): Promise<
  | {
      hotel: StaticVenue;
      candidate: EventVenueCandidate;
    }[]
  | null
> {
  // First get venue candidates with their static venue data, including chain and brand info
  const { data: venueData, error } = await supabase
    .schema("nowadays")
    .from("event_venue_candidate")
    .select(
      `
      *, 
      venue_static!event_venue_candidate_venue_static_id_fkey(
        *,
        venue_chains:chain_id (
          chain_name
        ),
        venue_chain_brands:brand_id (
          brand_name
        )
      )
    `
    )
    .eq("event_id", event.event_id);

  if (error) {
    return null;
  }

  // Extract venue_static_ids
  const venueStaticIds = venueData.map((venue) => venue.venue_static_id);

  // Get geocoding data for these venues
  const { data: geocodeData } = await supabase
    .schema("nowadays")
    .from("venue_static_geocoded")
    .select("*")
    .in("venue_static_id", venueStaticIds);

  // Map the venue data
  return venueData.map((values) => {
    const { venue_static, ...candidate } = values;

    // Extract chain and brand names from joins and exclude nested objects
    let hotel = null;
    if (venue_static) {
      const { venue_chains, venue_chain_brands, ...hotelData } = venue_static;
      hotel = {
        ...hotelData,
        chain_name: venue_chains?.chain_name || null,
        brand_name: venue_chain_brands?.brand_name || null,
      };
    }

    // Add geocoding data if available
    if (geocodeData) {
      const venueGeocode = geocodeData.find((g) => g.venue_static_id === values.venue_static_id);
      if (venueGeocode) {
        hotel.geocode = {
          latitude: venueGeocode.latitude,
          longitude: venueGeocode.longitude,
          display_name: venueGeocode.display_name,
        };
      }
    }

    return {
      candidate: candidate,
      hotel: hotel,
    };
  });
}

export async function getStaticVenue(supabase: SupabaseClient, venue_static_id: number): Promise<StaticVenue | null> {
  const { data, error } = await supabase
    .schema("nowadays")
    .from("venue_static")
    .select(
      `
      *,
      venue_chains:chain_id (
        chain_name
      ),
      venue_chain_brands:brand_id (
        brand_name
      )
    `
    )
    .eq("venue_static_id", venue_static_id);
  if (error) {
    return null;
  }
  if (!data || data.length === 0) {
    return null;
  }
  // Extract chain and brand names from joins and exclude nested objects
  const { venue_chains, venue_chain_brands, ...venue } = data[0];

  return {
    ...venue,
    chain_name: venue_chains?.chain_name || null,
    brand_name: venue_chain_brands?.brand_name || null,
  };
}

export async function getStaticVenues(
  supabase: SupabaseClient,
  filters?: { city?: string; state?: string; country?: string }
): Promise<StaticVenue[] | null> {
  let query = supabase.schema("nowadays").from("venue_static").select("*");

  if (filters?.city) {
    query = query.eq("city", filters.city);
  }
  if (filters?.state) {
    query = query.eq("state", filters.state);
  }
  if (filters?.country) {
    query = query.eq("country", filters.country);
  }

  const { data, error } = await query;
  if (error) {
    return null;
  }
  return data ?? null;
}

export async function areVenueImagesVerified(supabase: SupabaseClient, venue_static_id: number): Promise<boolean> {
  const { data, error } = await supabase
    .schema("nowadays")
    .from("venue_static")
    .select("photos_manually_verified")
    .eq("venue_static_id", venue_static_id)
    .single();

  if (error) {
    console.error("Error checking if venue images are verified:", JSON.stringify(error, null, 2));
    throw error;
  }

  return data.photos_manually_verified;
}

export async function getHotelsWithUnverifiedImages(supabase: SupabaseClient): Promise<number[] | null> {
  const { data, error } = await supabase
    .schema("nowadays")
    .from("venue_static")
    .select("venue_static_id")
    .is("photos_manually_verified", null);

  if (error) {
    console.error("Error fetching hotels with unverified images:", JSON.stringify(error, null, 2));
    return null;
  }

  const hotelIdsWithoutVerifiedImages = data.map((hotel) => hotel.venue_static_id);

  return hotelIdsWithoutVerifiedImages;
}

// CUSTOMER QUESTION AND ANSWER FUNCTIONS

// Update these functions

export async function getCustomerVenueQuestions(
  supabase: SupabaseClient,
  eventId: number
): Promise<{ question_id: number; question: string }[]> {
  const { data, error } = await supabase
    .schema("nowadays")
    .from("customer_venue_questions")
    .select("question_id, question")
    .eq("event_id", eventId);

  if (error) {
    console.error("Error fetching customer venue questions:", error);
    return [];
  }

  return data;
}

export async function getCustomerVenueAnswers(
  supabase: SupabaseClient,
  eventId: number,
  venueStaticId: number
): Promise<{ question_id: number; answer: string }[]> {
  const { data, error } = await supabase
    .schema("nowadays")
    .from("customer_venue_answers")
    .select("question_id, answer")
    .eq("venue_static_id", venueStaticId)
    .eq("event_id", eventId);

  if (error) {
    console.error("Error fetching customer venue answers:", error);
    return [];
  }

  return data;
}

// Function to add a new question for an event
export async function addEventQuestion(supabase: SupabaseClient, eventId: number, question: string): Promise<void> {
  const { error } = await supabase
    .schema("nowadays")
    .from("customer_venue_questions")
    .insert([{ event_id: eventId, question }]);

  if (error) {
    console.error("Error adding event question:", error);
    throw error;
  }
}

// Function to save or update the answer to the database
export async function addOrEditEventAnswer(
  supabase: SupabaseClient,
  eventId: number,
  venueId: number,
  questionId: number,
  answer: string
) {
  // Check if the answer already exists
  const { data: existingAnswer, error: fetchError } = await supabase
    .schema("nowadays")
    .from("customer_venue_answers")
    .select("answer")
    .eq("venue_static_id", venueId)
    .eq("question_id", questionId)
    .single();

  if (fetchError && fetchError.code !== "PGRST116") {
    // Handle error if not "not found"
    console.error("Error fetching existing answer:", fetchError);
    throw fetchError; // Handle the error as needed
  }

  // Prepare the data for update
  const answerData = {
    venue_static_id: venueId,
    question_id: questionId,
    answer,
  };

  if (existingAnswer) {
    // If the answer exists, update it
    const { error } = await supabase
      .schema("nowadays")
      .from("customer_venue_answers")
      .update({ answer }) // Update the answer field
      .eq("venue_static_id", venueId)
      .eq("question_id", questionId);

    if (error) {
      console.error("Error updating answer:", error);
      throw error; // Handle the error as needed
    }
  } else {
    // If the answer does not exist, insert a new one
    const newAnswerData = {
      ...answerData,
      event_id: eventId,
    };

    const { error } = await supabase.schema("nowadays").from("customer_venue_answers").insert([newAnswerData]);

    if (error) {
      console.error("Error inserting new answer:", error);
      throw error; // Handle the error as needed
    }
  }

  return answerData; // Return the answer data
}

// Function to update an existing question
export async function updateEventQuestion(
  supabase: SupabaseClient,
  questionId: number,
  question: string
): Promise<void> {
  const { error } = await supabase
    .schema("nowadays")
    .from("customer_venue_questions")
    .update({ question })
    .eq("question_id", questionId);

  if (error) {
    console.error("Error updating event question:", error);
    throw error;
  }
}

export async function referCustomer(
  supabase: SupabaseClient,
  fullName: string,
  email: string,
  referralName: string,
  referralEmail: string,
  whyGoodFit: string
): Promise<void> {
  const { error } = await supabase
    .schema("nowadays")
    .from("customer_referrals")
    .insert([
      {
        full_name: fullName,
        email: email,
        referral_name: referralName,
        referral_email: referralEmail,
        why_good_fit: whyGoodFit,
        created_at: new Date().toISOString(), // Add explicit timestamp
      },
    ]);

  await notifyCustomerReferred({ fullName, email, referralName, referralEmail, whyGoodFit });

  if (error) {
    console.error("Error referring customer:", JSON.stringify(error, null, 2));
  }
}

export async function vendorSignup(
  supabase: SupabaseClient,
  fullName: string,
  companyName: string,
  website: string,
  workEmail: string,
  phone: string,
  description: string
): Promise<void> {
  const { error } = await supabase
    .schema("nowadays")
    .from("vendor_signups")
    .insert([
      {
        full_name: fullName,
        company_name: companyName,
        website: website,
        email: workEmail,
        phone: phone,
        description: description,
        created_at: new Date().toISOString(), // Add explicit timestamp
      },
    ]);

  await notifyVendorSignedUp({ fullName, companyName, website, workEmail, phone, description });

  if (error) {
    console.error("Error signing up vendor:", JSON.stringify(error, null, 2));
  }
}

/** Details a partner submits through the /partners sign-up form. */
export interface VenuePartnerSignup {
  fullName: string;
  email: string;
  role: string;
  /** Answer to "What best describes you?". */
  partnerType: PartnerType;
  /** Answer to "Please specify" when the partner type is "other". Empty string otherwise. */
  partnerTypeOther: string;
  propertyName: string;
  propertyWebsite: string;
  /** Optional answer to "How did you hear about us?". Empty string when skipped. */
  referralSource: string;
}

/**
 * Saves a partner sign-up and notifies the website Slack channel.
 * The partner's name is stored in the `company_name` column, and "How did you hear about us?"
 * in the `description` column.
 *
 * @param supabase - Browser Supabase client (anon or authenticated).
 * @param signup - The submitted form values.
 * @returns Resolves once the row is saved and Slack is notified.
 * @throws When the insert fails, so the form can show an error instead of the success modal.
 */
export async function venuePartner(supabase: SupabaseClient, signup: VenuePartnerSignup): Promise<void> {
  const { fullName, email, role, partnerType, partnerTypeOther, propertyName, propertyWebsite, referralSource } =
    signup;

  const { error } = await supabase
    .schema("nowadays")
    .from("venue_partners")
    .insert([
      {
        full_name: fullName,
        email,
        role,
        partner_type: partnerType,
        partner_type_other: partnerTypeOther || null,
        company_name: propertyName,
        website: propertyWebsite,
        description: referralSource || null,
      },
    ]);

  if (error) {
    console.error("Error signing up venue partner:", JSON.stringify(error, null, 2));
    throw new Error(error.message);
  }

  // The row is saved, so a Slack failure must not turn into a failed submit for the venue.
  await notifyPartnerSignedUp({
    fullName,
    email,
    role,
    partnerType,
    partnerTypeOther,
    propertyName,
    propertyWebsite,
    referralSource,
  }).catch((slackError) => {
    console.error("venuePartner: slack notify failed", { email }, slackError);
  });
}

export async function newsletterSignup(
  supabase: SupabaseClient,
  firstName: string,
  lastName: string,
  email: string,
  comments: string,
  phone?: string
): Promise<void> {
  const { error } = await supabase
    .schema("nowadays")
    .from("newsletter")
    .insert([
      {
        first_name: firstName,
        last_name: lastName,
        email: email,
        comments: comments,
        created_at: new Date().toISOString(), // Add explicit timestamp
      },
    ]);

  if (error) {
    console.error("Error signing up for newsletter:", JSON.stringify(error, null, 2));
    throw error; // Re-throw the error so the UI can handle it
  }

  // Send Slack notification
  await notifyNewsletterSignedUp({ firstName, lastName, email, phone, comments });

  // Send confirmation email
  try {
    const response = await fetch("/api/newsletter/confirm", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        firstName,
        lastName,
        email,
      }),
    });

    if (!response.ok) {
      console.error("Failed to send newsletter confirmation email:", response.statusText);
      // Don't throw here as the main signup was successful
    }
  } catch (emailError) {
    console.error("Error sending newsletter confirmation email:", emailError);
    // Don't throw here as the main signup was successful
  }
}

export async function createDraftEvent(
  supabase: SupabaseClient,
  data: Omit<DraftEvents, "draft_event_id" | "created_at">
): Promise<DraftEvents | null> {
  const { data: insertedData, error } = await supabase
    .schema("nowadays")
    .from("draft_events")
    .insert([data])
    .select("*")
    .single();

  if (error || !insertedData) {
    console.error("Error creating draft event:", JSON.stringify(error, null, 2));
    return null;
  }

  return insertedData;
}

export async function getDraftEvent(supabase: SupabaseClient, draft_event_id: number): Promise<DraftEvents | null> {
  const { data, error } = await supabase
    .schema("nowadays")
    .from("draft_events")
    .select("*")
    .eq("draft_event_id", draft_event_id)
    .maybeSingle();

  if (error) {
    console.error("Error fetching draft event:", error);
    return null;
  }

  return data;
}

export async function getDraftEventByUuid(supabase: SupabaseClient, uuid: string): Promise<DraftEvents | null> {
  const { data, error } = await supabase
    .schema("nowadays")
    .from("draft_events")
    .select("*")
    .eq("uuid", uuid)
    .maybeSingle();

  if (error) {
    console.error("Error fetching draft event by UUID:", error);
    return null;
  }

  return data;
}

// Helper function to format venue data for Slack messages
function formatVenueDataForSlack(venueData: any[] | null | undefined): Array<{ title: string; message: string }> {
  if (!venueData || !Array.isArray(venueData) || venueData.length === 0) {
    return [
      { title: "Northstar Venues", message: "None" },
      { title: "Custom/Google Venues", message: "None" },
    ];
  }

  // Filter Northstar venues first (these should NOT appear in Custom/Google)
  const northstarVenues = venueData.filter((venue: any) => venue.source === "northstar" || venue.ntm_id);

  // Filter Custom/Google venues - exclude any that are Northstar venues
  const customVenues = venueData.filter(
    (venue: any) =>
      (venue.source === "google" || venue.source === "custom_venue" || venue.google_place_id) &&
      venue.source !== "northstar" &&
      !venue.ntm_id
  );

  const formatVenueList = (venues: any[]) =>
    venues.length > 0
      ? venues.map((venue: any) => `${venue.venue_name ?? ""} (${venue.venue_city ?? ""})`).join(", ")
      : "None";

  return [
    { title: "Northstar Venues", message: formatVenueList(northstarVenues) },
    { title: "Custom/Google Venues", message: formatVenueList(customVenues) },
  ];
}

export async function updateDraftEvent(
  supabase: SupabaseClient,
  draft_event_id: number,
  data: Omit<DraftEvents, "event_id" | "org_id" | "created_at" | "event_status" | "event_status_last_update">,
  sendSlack: boolean = true,
  trackChanges: boolean = true
): Promise<DraftEvents | null> {
  // Get existing draft event data to compare changes
  let existingData: DraftEvents | null = null;
  if (trackChanges) {
    const { data: existing, error: fetchError } = await supabase
      .schema("nowadays")
      .from("draft_events")
      .select("*")
      .eq("draft_event_id", draft_event_id)
      .single();

    if (!fetchError && existing) {
      existingData = existing;
    }
  }

  const dateRange = getEventDateRange(data);
  const isSameDay = dateRange ? moment.utc(dateRange.startDate).isSame(moment.utc(dateRange.endDate), "day") : false;

  if (sendSlack) {
    sendSlackMessage(
      SlackNotificationType.EVENT,
      "ATTEMPTING TO UPDATE DRAFT EVENT",
      `Northstar User updated a draft event!`,
      [
        { title: "Draft Event ID", message: draft_event_id.toString() },
        { title: "Event Name", message: data.name ?? "" },
        {
          title: isSameDay ? "Event Date" : "Start Date",
          message: dateRange ? moment.utc(dateRange.startDate).format("MM/DD/YYYY") : "Not set",
        },
        {
          title: isSameDay ? "Time" : "End Date",
          message: isSameDay
            ? "Time not specified" // TODO: Extract time info from event_dates if needed
            : dateRange
              ? moment.utc(dateRange.endDate).format("MM/DD/YYYY")
              : "Not set",
        },
        { title: "Location", message: data.location ?? "" },
        {
          title: "Number of People",
          message: data.num_people?.toString() ?? "",
        },
        { title: "Budget", message: ((data as any).budget_breakdown?.venue || 0).toString() },
        {
          title: "Accommodations",
          message: formatAccommodationsForSlack(data.accommodations),
        },
        {
          title: "Decision Deadline",
          message: data.decision_deadline ? moment.utc(data.decision_deadline).format("MM/DD/YYYY") : "Not specified",
        },
        { title: "Venue Type", message: data.venue_type ?? "" },
        { title: "Flexibility", message: data.flexibility ?? "" },
        { title: "Other Notes", message: data.other_notes ?? "" },
        { title: "Meeting Rooms", message: data.meeting_rooms ?? "" },
        {
          title: "Food and Beverage Requirements",
          message: data.food_beverage_requirements ?? "",
        },
        ...formatVenueDataForSlack(data.venue_data),
      ]
    );
  }

  const { data: insertedData, error } = await supabase
    .schema("nowadays")
    .from("draft_events")
    .update([
      {
        ...data,
      },
    ])
    .eq("draft_event_id", draft_event_id)
    .select();

  if (error || !insertedData) {
    console.error("Error creating event:", JSON.stringify(error, null, 2));
    return null;
  }

  const createdEvent = insertedData[0];

  // Track changes if enabled and we have existing data
  if (trackChanges && existingData) {
    const changes: Array<{
      field_name: keyof DraftEvents;
      old_value: string | null;
      new_value: string | null;
    }> = [];

    // Helper function to serialize values for comparison
    const serializeValue = (value: any): string | null => {
      if (value === null || value === undefined) return null;
      if (typeof value === "object") return JSON.stringify(value);
      if (value instanceof Date) return value.toISOString();
      return String(value);
    };

    // Compare each field and track changes
    (Object.keys(data) as Array<keyof typeof data>).forEach((key) => {
      const oldValue = serializeValue(existingData[key as keyof DraftEvents]);
      const newValue = serializeValue(data[key]);

      if (oldValue !== newValue) {
        changes.push({
          field_name: key as keyof DraftEvents,
          old_value: oldValue,
          new_value: newValue,
        });
      }
    });
  }

  // Check for high budget notification (>$50K)
  // Only send if budget_breakdown was actually updated in this call
  const budgetWasUpdated = (data as any).budget_breakdown !== undefined;
  const newBudget = (data as any).budget_breakdown?.venue || (createdEvent as any).budget_breakdown?.venue || 0;
  const oldBudget = existingData ? (existingData as any).budget_breakdown?.venue || 0 : 0;
  const budgetThreshold = 50000;
  const isHighBudget = newBudget > budgetThreshold;

  // Send high budget notification if:
  // 1. Budget field was updated in this call
  // 2. New budget is >$50K
  // 3. Budget actually changed (to avoid duplicate notifications)
  if (budgetWasUpdated && isHighBudget && newBudget !== oldBudget) {
    try {
      // The server reads the draft by id and builds the alert, so the browser sends no message text.
      await notifyHighValueDraft({ draftEventId: draft_event_id });
    } catch (error) {
      console.error("Error sending high budget notification:", error);
      // Don't fail the update if notification fails
    }
  }

  if (sendSlack) {
    await sendSlackMessage(
      SlackNotificationType.EVENT,
      "UPDATED DRAFT EVENT",
      `Northstar User updated a draft event!`,
      []
    );
  }

  return createdEvent;
}

// Helper function to send rich Slack notification
async function sendRichSlackNotification(
  type: SlackNotificationType,
  message: string,
  title: string,
  titleLink: string,
  fields: Array<{ title: string; value: string; short: boolean }>,
  color: string = "#36a64f"
): Promise<void> {
  const webhookUrl = process.env[`SLACK_WEBHOOK_URL_${type}`];
  if (!webhookUrl) {
    console.warn(`SLACK_WEBHOOK_URL_${type} not configured, falling back to simple format`);
    // Fallback to simple format
    await sendSlackMessage(
      type,
      title,
      message,
      fields.map((d) => ({ title: d.title, message: d.value }))
    );
    return;
  }

  const payload = {
    text: message,
    attachments: [
      {
        color: color, // Ensure color is set for the sidebar
        title,
        title_link: titleLink,
        fields,
        footer: "Event Creation System",
        footer_icon: "https://nowadays.ai/favicon.ico",
        ts: Math.floor(Date.now() / 1000),
      },
    ],
  };

  try {
    const response = await fetch(webhookUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Slack webhook returned ${response.status}: ${errorText}`);
    }
  } catch (error) {
    console.error("Error sending rich Slack notification, falling back to simple format:", error);
    // Fallback to simple format
    await sendSlackMessage(
      type,
      title,
      message,
      fields.map((d) => ({ title: d.title, message: d.value }))
    );
  }
}

/**
 * Notifies the team about a newly created event, adds its venues, sends the RFPs, and sends the
 * client confirmation email.
 *
 * @param supabase - Service-role client
 * @param realEventData - The created event
 * @param draftEvent - The draft it was created from
 * @param user - The event owner (the confirmation email recipient)
 * @param createdByAdmin - The admin who created it for someone else, shown in Slack
 * @param sendConfirmationEmail - Server-side sender for the client confirmation email. This module
 *   is also bundled for the browser, so the server caller injects it; when omitted, no email is sent
 * @returns Resolves when processing finishes
 */
export async function processVenueDataAndNotify(
  supabase: SupabaseClient,
  realEventData: Events,
  draftEvent: DraftEvents,
  user: Users,
  createdByAdmin?: Users | null,
  sendConfirmationEmail?: (params: RFPEmailTemplateParams) => Promise<void>
) {
  try {
    // Send Slack Message to Admin - different message for Northstar vs regular Nowadays
    const isNorthstarEvent = draftEvent.is_northstar_event || realEventData.is_northstar_event;

    // Format phone number with country dial code
    const formatPhoneNumber = async (
      phone: string | undefined | null,
      countryDialCodeId?: number | null
    ): Promise<string> => {
      const { formatPhoneNumberWithDialCode } = await import("@/lib/utils/phone-formatting");
      return await formatPhoneNumberWithDialCode(phone, countryDialCodeId);
    };

    // Format budget breakdown
    const formatBudget = (budgetBreakdown: any): string => {
      if (!budgetBreakdown || !budgetBreakdown.venue || budgetBreakdown.venue === 0) {
        return "Not specified";
      }
      return `$${budgetBreakdown.venue.toLocaleString()}`;
    };

    // Format array fields
    const formatArray = (arr: any[] | string | undefined | null): string => {
      if (!arr) return "None";
      if (typeof arr === "string") return arr;
      if (Array.isArray(arr) && arr.length === 0) return "None";
      if (Array.isArray(arr)) return arr.join(", ");
      return String(arr);
    };

    // Build details with sections
    const adminLink = adminEventUrl(realEventData.event_id);
    const details: Array<{ title: string; value: string; short: boolean }> = [];

    // INPUT section
    details.push({
      title: "INPUT",
      value: "",
      short: false,
    });

    details.push({
      title: "Event ID",
      value: realEventData.event_id.toString(),
      short: true,
    });

    details.push({
      title: "Admin Dash Link",
      value: `<${adminLink}|View Event>`,
      short: true,
    });

    // CONTACT INFORMATION section
    details.push({
      title: "CONTACT INFORMATION",
      value: "",
      short: false,
    });

    // Show who created the event if admin created it for someone else
    if (createdByAdmin && createdByAdmin.email !== user.email) {
      details.push({
        title: "Created By (Admin)",
        value: `${createdByAdmin.first_name} ${createdByAdmin.last_name} (${createdByAdmin.email})`,
        short: false,
      });
    }

    details.push({
      title: "Name",
      value: `${user.first_name} ${user.last_name}`,
      short: true,
    });

    details.push({
      title: "Email",
      value: user.email || "Not provided",
      short: true,
    });

    const formattedPhone = await formatPhoneNumber(user.phone, user.country_dial_code_id);
    details.push({
      title: "Phone Number",
      value: formattedPhone,
      short: true,
    });

    details.push({
      title: "Title",
      value: user.title || "Not provided",
      short: true,
    });

    details.push({
      title: "Company",
      value: user.company || "Not provided",
      short: true,
    });

    details.push({
      title: "Industry",
      value: user.industry || "Not provided",
      short: true,
    });

    details.push({
      title: "Headquarters City",
      value: user.headquarters_city || "Not provided",
      short: true,
    });

    // EVENT DETAILS section
    details.push({
      title: "EVENT DETAILS",
      value: "",
      short: false,
    });

    details.push({
      title: "Event Name",
      value: realEventData.name || "Not provided",
      short: true,
    });

    details.push({
      title: "Location",
      value: realEventData.location || "Not provided",
      short: true,
    });

    details.push({
      title: "Event Dates",
      value: formatEventDatesForSlack(realEventData),
      short: false,
    });

    details.push({
      title: "Number of People",
      value: realEventData.num_people?.toString() || "0",
      short: true,
    });

    details.push({
      title: "Venue Type",
      value: realEventData.venue_type || "Not specified",
      short: true,
    });

    details.push({
      title: "Budget (Venue)",
      value: formatBudget((realEventData as any).budget_breakdown),
      short: true,
    });

    if ((realEventData as any).budget_breakdown) {
      const budget = (realEventData as any).budget_breakdown;
      if (budget.flight || budget.fb || budget.transport || budget.activities || budget.other) {
        details.push({
          title: "Budget Breakdown",
          value: [
            budget.flight ? `Flight: $${budget.flight.toLocaleString()}` : null,
            budget.fb ? `F&B: $${budget.fb.toLocaleString()}` : null,
            budget.transport ? `Transport: $${budget.transport.toLocaleString()}` : null,
            budget.activities ? `Activities: $${budget.activities.toLocaleString()}` : null,
            budget.other ? `Other: $${budget.other.toLocaleString()}` : null,
          ]
            .filter(Boolean)
            .join("\n"),
          short: false,
        });
      }
    }

    details.push({
      title: "Accommodations",
      value: formatAccommodationsForSlack(realEventData.accommodations),
      short: false,
    });

    details.push({
      title: "Decision Deadline",
      value: realEventData.decision_deadline
        ? moment.utc(realEventData.decision_deadline).format("MM/DD/YYYY")
        : "Not specified",
      short: true,
    });

    details.push({
      title: "Flexibility",
      value: realEventData.flexibility || "Not specified",
      short: true,
    });

    // ADDITIONAL DETAILS section
    if (
      realEventData.origin ||
      realEventData.meeting_rooms ||
      realEventData.food_beverage_requirements ||
      realEventData.other_notes ||
      realEventData.notes_location_budget
    ) {
      details.push({
        title: "ADDITIONAL DETAILS",
        value: "",
        short: false,
      });

      if (realEventData.origin) {
        details.push({
          title: "Origin",
          value: realEventData.origin,
          short: true,
        });
      }

      if (realEventData.meeting_rooms) {
        details.push({
          title: "Meeting Rooms",
          value: realEventData.meeting_rooms,
          short: true,
        });
      }

      if (realEventData.food_beverage_requirements) {
        details.push({
          title: "Food & Beverage Requirements",
          value: realEventData.food_beverage_requirements,
          short: false,
        });
      }

      if (realEventData.other_notes) {
        details.push({
          title: "Other Notes",
          value: realEventData.other_notes,
          short: false,
        });
      }

      if (realEventData.notes_location_budget) {
        details.push({
          title: "Notes on Location & Budget",
          value: realEventData.notes_location_budget,
          short: false,
        });
      }
    }

    // OPTIONS & PREFERENCES section
    if (
      realEventData.event_source ||
      realEventData.decision_factors ||
      realEventData.additional_services ||
      realEventData.custom_questions ||
      realEventData.custom_concessions
    ) {
      details.push({
        title: "OPTIONS & PREFERENCES",
        value: "",
        short: false,
      });

      if (realEventData.event_source) {
        details.push({
          title: "Event Source",
          value: realEventData.event_source_other_text
            ? `${realEventData.event_source} - ${realEventData.event_source_other_text}`
            : realEventData.event_source,
          short: true,
        });
      }

      if (
        realEventData.decision_factors &&
        Array.isArray(realEventData.decision_factors) &&
        realEventData.decision_factors.length > 0
      ) {
        details.push({
          title: "Decision Factors",
          value: realEventData.decision_factor_other_text
            ? `${realEventData.decision_factors.join(", ")} - ${realEventData.decision_factor_other_text}`
            : realEventData.decision_factors.join(", "),
          short: false,
        });
      }

      if (
        realEventData.additional_services &&
        Array.isArray(realEventData.additional_services) &&
        realEventData.additional_services.length > 0
      ) {
        details.push({
          title: "Additional Services",
          value: formatArray(realEventData.additional_services),
          short: false,
        });
      }

      if (
        realEventData.custom_questions &&
        Array.isArray(realEventData.custom_questions) &&
        realEventData.custom_questions.length > 0
      ) {
        details.push({
          title: "Custom Questions",
          value: formatArray(realEventData.custom_questions),
          short: false,
        });
      }

      if (
        realEventData.custom_concessions &&
        Array.isArray(realEventData.custom_concessions) &&
        realEventData.custom_concessions.length > 0
      ) {
        details.push({
          title: "Custom Concessions",
          value: formatArray(realEventData.custom_concessions),
          short: false,
        });
      }
    }

    // Add venue data
    const venueDataFields = formatVenueDataForSlack(draftEvent.venue_data);
    if (venueDataFields.length > 0) {
      details.push({
        title: "VENUE DATA",
        value: "",
        short: false,
      });
      venueDataFields.forEach((field) => {
        details.push({
          title: field.title,
          value: field.message,
          short: false,
        });
      });
    }

    // Build message
    let slackMessage = `${user.first_name} ${user.last_name} created a new event!`;
    if (isNorthstarEvent) {
      const mentions = await getNorthstarPocsSlackMentions(supabase);
      slackMessage = `${mentions} A new Northstar event has been created by ${user.first_name} ${user.last_name}!`;
    }

    // Send rich Slack notification
    await sendRichSlackNotification(
      isNorthstarEvent ? SlackNotificationType.NORTHSTAR : SlackNotificationType.EVENT,
      slackMessage,
      isNorthstarEvent
        ? `🆕 New Northstar Event: ${realEventData.name}`
        : `🎉 New Nowadays Event: ${realEventData.name}`,
      adminLink,
      details,
      isNorthstarEvent ? "#7B68EE" : "#36a64f"
    );

    // Process venue_data if it exists and add them as candidates to the event

    if (draftEvent.venue_data && Array.isArray(draftEvent.venue_data)) {
      // Filter valid venues - all venues should now have venue_static_id
      const venuesToProcess = draftEvent.venue_data.filter((venue: any) =>
        Boolean(venue.venue_static_id && venue.venue_name && venue.venue_city)
      );

      // Check for venues that need manual review (couldn't be matched)
      const venuesNeedingReview = draftEvent.venue_data.filter((venue: any) =>
        Boolean(venue.needs_manual_review && venue.venue_name && venue.venue_city)
      );

      if (venuesNeedingReview.length > 0) {
        console.warn("Venues requiring manual review:", venuesNeedingReview.length);
        venuesNeedingReview.forEach((v: any) => {
          console.warn(`- ${v.venue_name} (${v.venue_city}) - NTM ID: ${v.ntm_id || "N/A"}`);
        });

        // Send Slack notification about venues needing review (best-effort; a failed alert must not
        // stop the venues that did match from being added below)
        await notifyVenuesNeedManualReview({
          eventId: realEventData.event_id,
          venues: venuesNeedingReview.map((v: any) => ({
            name: v.venue_name,
            city: v.venue_city,
            ntmId: v.ntm_id ?? null,
          })),
        }).catch((notifyError) => {
          console.error("Failed to send venues-need-review notification", notifyError);
        });
      }

      if (venuesToProcess.length === 0) {
        return;
      }

      // Determine if there are any Northstar venues
      const hasNorthstarVenues = venuesToProcess.some((v: any) => v.source === "northstar" || v.ntm_id);

      // Assign rankings to all venues
      const venuesWithRankings = venuesToProcess.map((venue: any, index: number) => {
        const source = venue.source || "google";
        const isNorthstarVenue = source === "northstar" || venue.ntm_id;

        let ranking: number;
        if (isNorthstarVenue) {
          ranking = 1; // Northstar venues always get rank 1
        } else {
          const nonNorthstarIndexBefore = venuesToProcess
            .slice(0, index)
            .filter((v: any) => (v.source || "google") !== "northstar").length;

          if (hasNorthstarVenues) {
            ranking = nonNorthstarIndexBefore === 0 ? 2 : 3 + nonNorthstarIndexBefore - 1;
          } else {
            ranking = nonNorthstarIndexBefore === 0 ? 1 : 3 + nonNorthstarIndexBefore - 1;
          }
        }

        return { ...venue, assignedRanking: ranking };
      });

      // Add venues to event as candidates - all venues now have venue_static_id so we can skip fuzzy matching
      const successfulVenueNames: string[] = [];
      const failedVenueNames: string[] = [];

      // Extract display name from first formatted location for distance display
      // AutoDash will later calculate actual distances and update minutes/mode
      const formattedLocations = realEventData.formatted_locations as FormattedLocation[] | null;
      const customOriginName = formattedLocations?.[0]?.display_name || null;

      const venuePromises = venuesWithRankings.map(async (venue: any) => {
        const { venue_static_id, venue_name, venue_city, assignedRanking, source, ...otherFields } = venue;
        const venueDisplayName = `${venue_name} (${venue_city})`;

        try {
          // Use the standard function to add venue as candidate
          const candidate = await addVenueToEventVenueCandidate(
            supabase,
            realEventData.event_id,
            venue_static_id,
            undefined, // minutesFromOrigin (set by AutoDash distance calc)
            undefined, // modeOfTravelFromOrigin (set by AutoDash distance calc)
            customOriginName, // customOriginName (e.g., "Atlanta Office")
            true, // showOnDash
            source || "google", // source
            assignedRanking, // ranking
            true, // markedAsLiked
            user.user_id, // markedAsLikedUserId
            true // customerAdded
          );

          if (!candidate) {
            console.error(`Error adding candidate for ${venue_name}`);
            failedVenueNames.push(venueDisplayName);
            return null;
          }

          successfulVenueNames.push(venueDisplayName);

          // Get the static venue
          const staticVenue = await getStaticVenue(supabase, venue_static_id);
          if (!staticVenue) {
            console.error(`Error fetching static venue for ${venue_name}`);
            // Remove from successful since we couldn't get the venue
            const index = successfulVenueNames.indexOf(venueDisplayName);
            if (index > -1) successfulVenueNames.splice(index, 1);
            failedVenueNames.push(venueDisplayName);
            return null;
          }

          return {
            venue: staticVenue,
            candidate,
            quotes: [],
            email_threads: [],
          } as VenueOption;
        } catch (error) {
          console.error(`Error processing venue ${venue_name}:`, error);
          failedVenueNames.push(venueDisplayName);
          return null;
        }
      });

      const venueOptions = (await Promise.all(venuePromises)).filter((v): v is VenueOption => v !== null);

      // Check if some venues failed to add (partial success)
      if (venueOptions.length > 0 && venueOptions.length < venuesToProcess.length) {
        const isNorthstarEvent = draftEvent.is_northstar_event || realEventData.is_northstar_event;
        const failedCount = venuesToProcess.length - venueOptions.length;

        await sendSlackMessage(
          isNorthstarEvent ? SlackNotificationType.NORTHSTAR : SlackNotificationType.EVENT,
          "⚠️ PARTIAL VENUE ADDITION SUCCESS",
          `${venueOptions.length} of ${venuesToProcess.length} venues were successfully added to the event dashboard. ${failedCount} venue(s) failed to add and need manual review.`,
          [
            { title: "Event ID", message: realEventData.event_id.toString() },
            { title: "Event Name", message: realEventData.name ?? "" },
            { title: "Admin Dash Link", message: adminEventUrl(realEventData.event_id) },
            {
              title: "✅ Successfully Added Venues",
              message: successfulVenueNames.length > 0 ? successfulVenueNames.join("\n") : "None",
            },
            {
              title: "❌ Failed Venues",
              message: failedVenueNames.length > 0 ? failedVenueNames.join("\n") : "None",
            },
            {
              title: "What This Means",
              message:
                "Some venues were added to the event dashboard and RFPs will be sent automatically. The failed venues need to be manually added or reviewed in the admin dashboard.",
            },
          ]
        );
      }

      if (venueOptions.length === 0) {
        // Send Slack notification about no venues being successfully added
        const isNorthstarEvent = draftEvent.is_northstar_event || realEventData.is_northstar_event;
        const attemptedVenueNames = venuesToProcess.map((v: any) => `${v.venue_name} (${v.venue_city})`).join("\n");

        await sendSlackMessage(
          isNorthstarEvent ? SlackNotificationType.NORTHSTAR : SlackNotificationType.EVENT,
          "⚠️ NO VENUES ADDED TO EVENT",
          `Event was created but none of the ${venuesToProcess.length} venue(s) could be added to the event dashboard. All venues failed during processing.`,
          [
            { title: "Event ID", message: realEventData.event_id.toString() },
            { title: "Event Name", message: realEventData.name ?? "" },
            { title: "Admin Dash Link", message: adminEventUrl(realEventData.event_id) },
            {
              title: "❌ Failed Venues",
              message: failedVenueNames.length > 0 ? failedVenueNames.join("\n") : attemptedVenueNames || "None",
            },
            {
              title: "What This Means",
              message:
                "The event was created but no venues were added to the dashboard. This could be due to database errors, missing venue data, or other processing issues. Venues need to be manually added in the admin dashboard.",
            },
          ]
        );

        return;
      }

      if (!isTestEvent(realEventData)) {
        // Check if skip_sending_rfp flag is set (admin-only feature)
        if (draftEvent.skip_sending_rfp) {
          await sendSlackMessage(
            draftEvent.is_northstar_event || realEventData.is_northstar_event
              ? SlackNotificationType.NORTHSTAR
              : SlackNotificationType.EVENT,
            "[SKIPPED] RFP Emails Skipped",
            `RFP emails were skipped for this event (skip_sending_rfp flag enabled)`,
            [
              { title: "Event Name", message: realEventData.name ?? "" },
              { title: "Event ID", message: realEventData.event_id.toString() },
            ]
          );
        } else {
          const rfpResult = await sendRFPEmailsAutomatically(supabase, realEventData, user, venueOptions);

          if (rfpResult) {
            // Send confirmation email after RFPs are sent
            const isNorthstarEvent = draftEvent.is_northstar_event || realEventData.is_northstar_event;

            try {
              // No server-side sender injected means no email (see the param's JSDoc).
              if (sendConfirmationEmail && isNorthstarEvent && venuesToProcess && venuesToProcess.length > 0) {
                // Northstar events with requested venue
                const requestedVenue = venuesToProcess[0]?.venue_name;
                if (requestedVenue) {
                  await sendConfirmationEmail({
                    event: realEventData,
                    user,
                    isNorthstar: true,
                    stage: RFPEmailStage.RFP_SENT,
                    likedVenues: venueOptions,
                    requestedVenue,
                  });
                }
              } else if (sendConfirmationEmail && !isNorthstarEvent) {
                // Non-Northstar events
                await sendConfirmationEmail({
                  event: realEventData,
                  user,
                  isNorthstar: false,
                  stage: RFPEmailStage.EVENT_CREATED,
                });
              }
            } catch (emailError) {
              console.error("[PROCESS-VENUES] Error sending confirmation email:", emailError);
              // Don't fail the entire operation if email fails
            }
          }
        }
      } else {
        const isNorthstarEvent = draftEvent.is_northstar_event || realEventData.is_northstar_event;
        await sendSlackMessage(
          isNorthstarEvent ? SlackNotificationType.NORTHSTAR : SlackNotificationType.EVENT,
          "[FLAGGED] TEST EVENT?",
          `Skipped RFP Emails for Test Event`,
          [
            { title: "Event Name", message: realEventData.name ?? "" },
            { title: "Event ID", message: realEventData.event_id.toString() },
          ]
        );
      }
    }
  } catch (error) {
    console.error("Error in async venue processing:", error);
  }
}

export async function moveDraftToRealEvent(supabase: SupabaseClient, draft_event_id: number): Promise<Events | null> {
  try {
    // Use the secure API route for moving draft to real event
    const response = await fetch("/api/events/move-draft-to-real", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        draft_event_id,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error("Error moving draft to real event:", errorText);
      return null;
    }

    const result = await response.json();
    return result.event;
  } catch (error) {
    console.error("Error moving draft to real event:", error);
    return null;
  }
}

// NOTE: updateEventRFPEmail was removed. Every draft write now
// goes through src/lib/rfp/draft.ts so the disclosure snapshot is written
// alongside the body (service-role only).

export async function updateTimestampRFPSent(
  supabase: SupabaseClient,
  venue_static_id: number,
  event_id: number,
  source: string
): Promise<void> {
  // Update the timestamp_rfp_sent field after sending the email
  const { error: timestampError } = await supabase
    .schema("nowadays")
    .from("event_venue_candidate")
    .update({ timestamp_rfp_sent: new Date().toISOString() }) // Use the appropriate date format
    .eq("venue_static_id", venue_static_id)
    .eq("event_id", event_id);

  if (timestampError) {
    console.error("Error updating timestamp_rfp_sent:", JSON.stringify(timestampError, null, 2));
    throw timestampError;
  }

  const user = await getCurrentUser(supabase);

  // update the nowadays.venue_communications table
  const { error: venueCommunicationError } = await supabase.schema("nowadays").from("venue_communications").insert({
    venue_static_id: venue_static_id,
    event_id: event_id,
    communication_type: "email",
    timestamp: new Date().toISOString(),
    notes: source,
    user_id: user?.user_id,
    source: source,
  });
}

export async function updateTimestampRFPSentFailed(
  supabase: SupabaseClient,
  venue_static_id: number,
  event_id: number
): Promise<void> {
  const { error: timestampError } = await supabase
    .schema("nowadays")
    .from("event_venue_candidate")
    .update({ timestamp_rfp_sent_failed: new Date().toISOString() })
    .eq("venue_static_id", venue_static_id)
    .eq("event_id", event_id);

  if (timestampError) {
    console.error("Error updating timestamp_rfp_sent_failed:", JSON.stringify(timestampError, null, 2));
    throw timestampError;
  }
}

export async function updateEventDepositAmount(
  supabase: SupabaseClient,
  event_id: number,
  depositAmount: number
): Promise<void> {
  // Get the current month in the format YYYY-MM-01
  const currentMonth = new Date();
  const formattedMonth = `${currentMonth.getUTCFullYear()}-${String(currentMonth.getUTCMonth() + 1).padStart(2, "0")}-01`;

  // Fetch the current event to check if month_closed is null
  const { data: eventData, error: fetchError } = await supabase
    .schema("nowadays")
    .from("events")
    .select("month_closed")
    .eq("event_id", event_id)
    .single();

  if (fetchError) {
    console.error("Error fetching event data:", JSON.stringify(fetchError, null, 2));
    throw fetchError;
  }

  // Prepare the update payload
  const updatePayload: any = { deposit_amount_paid: depositAmount };
  let monthClosedUpdated = false;

  if (eventData && eventData.month_closed === null) {
    updatePayload.month_closed = formattedMonth;
    updatePayload.internal_status = InternalEventStatus.ACTIVE;
    monthClosedUpdated = true;
  }

  const { error: updateError } = await supabase
    .schema("nowadays")
    .from("events")
    .update(updatePayload)
    .eq("event_id", event_id);

  if (updateError) {
    console.error("Error updating event:", JSON.stringify(updateError, null, 2));
    throw updateError;
  }

  if (monthClosedUpdated) {
    await sendSlackMessage(
      SlackNotificationType.BREX,
      "MONTH CLOSED UPDATE",
      `Month closed updated for event_id: ${event_id}`,
      [
        { title: "Event ID", message: event_id.toString() },
        { title: "New Month Closed", message: formattedMonth },
        {
          title: "Internal Event Status",
          message: updatePayload.internal_status,
        },
      ]
    );
  }
}

export async function getEventRFPEmail(
  supabase: SupabaseClient,
  event_id: number
): Promise<{ rfp_email_subject: string; rfp_email_body: string } | null> {
  const { data, error } = await supabase
    .schema("nowadays")
    .from("events")
    .select("rfp_email_subject, rfp_email_body")
    .eq("event_id", event_id)
    .single();

  if (error || !data) {
    console.error("Error fetching RFP email:", error);
    return null;
  }

  return {
    rfp_email_subject: data.rfp_email_subject,
    rfp_email_body: data.rfp_email_body,
  };
}

// Event edit functions have been moved to @/features/change-event-details/_logic/edit-event-fields.ts

export async function editEventOrgId(
  supabase: SupabaseClient,
  event: Events,
  org_id?: number
): Promise<boolean | null> {
  const { error } = await supabase
    .schema("nowadays")
    .from("events")
    .update({
      org_id: org_id ?? event.org_id,
    })
    .eq("event_id", event.event_id);
  if (error) {
    console.error("Error updating event:", JSON.stringify(error, null, 2));
    return null;
  }
  return true;
}

// Event edit functions have been moved to @/features/change-event-details/_logic/edit-event-fields.ts

/**
 * Best-effort read of an event's current status column, used for the "previous → new" line on the
 * admin Slack card. Never throws and never blocks the mutation it precedes.
 *
 * @param supabase The SupabaseClient instance.
 * @param eventId The ID of the event.
 * @param column Which status column to read.
 * @returns The current value, or `null` when it is unset or the read failed.
 */
export async function readEventStatusColumn(
  supabase: SupabaseClient,
  eventId: number,
  column: "event_status" | "internal_status"
): Promise<string | null> {
  const { data, error } = await supabase
    .schema("nowadays")
    .from("events")
    .select(column)
    .eq("event_id", eventId)
    .maybeSingle();
  if (error) {
    console.error("readEventStatusColumn:", { eventId, column }, error);
    return null;
  }
  const value = (data as Record<string, unknown> | null)?.[column];
  return typeof value === "string" ? value : null;
}

/**
 * Updates the internal event status for an event.
 *
 * @param supabase The SupabaseClient instance.
 * @param eventId The ID of the event to update.
 * @param internalStatus The new internal status to set.
 */
export async function updateInternalEventStatus(
  supabase: SupabaseClient,
  eventId: number,
  internalStatus: string
): Promise<void> {
  const previousValue = await readEventStatusColumn(supabase, eventId, "internal_status");

  const { error } = await supabase
    .schema("nowadays")
    .from("events")
    .update({ internal_status: internalStatus })
    .eq("event_id", eventId);

  if (error) {
    console.error("Error updating internal event status:", JSON.stringify(error, null, 2));
    throw error;
  }

  // Slack notification is best-effort and non-blocking
  notifyEventStatusChanged({ eventId, kind: "internal_status", newValue: internalStatus, previousValue }).catch(
    (notifyError) => {
      console.error("Error sending Slack notification for internal status update:", notifyError);
    }
  );
}
/**
 * Changes an event status. Should only work if the supabase
 * client is an admin.
 *
 * @param supabase
 * @param eventId
 * @param eventStatus
 */
export async function updateEventStatus(
  supabase: SupabaseClient,
  eventId: number,
  eventStatus: EventStatus
): Promise<void> {
  const previousValue = await readEventStatusColumn(supabase, eventId, "event_status");

  const { error } = await supabase
    .schema("nowadays")
    .from("events")
    .update({ event_status: eventStatus })
    .eq("event_id", eventId);

  if (error) {
    console.error("Error updating event status:", JSON.stringify(error, null, 2));
    throw error;
  }

  // Clear commission details if the event is cancelled
  if (eventStatus === EventStatus.CANCELLED) {
    await updateEventCommissionDetails(supabase, eventId, {
      expected_commission: null,
      date_expected_commission: null,
    });

    await updateInternalEventStatus(supabase, eventId, InternalEventStatus.CANCELED);
  }

  // Slack notification is best-effort and non-blocking
  notifyEventStatusChanged({ eventId, kind: "status", newValue: eventStatus, previousValue }).catch((notifyError) => {
    console.error("Error sending Slack notification for event status update:", notifyError);
  });
}
// Function to create a new org and return its ID
export async function createNewOrg(supabase: SupabaseClient): Promise<{ newOrgId: number; userId: number } | null> {
  const user = await getCurrentUser(supabase);

  if (!user) {
    return null;
  }

  // Find the maximum org_id
  const { data: maxData, error: maxError } = await supabase
    .schema("nowadays")
    .from("orgs")
    .select("org_id")
    .order("org_id", { ascending: false })
    .limit(1);

  if (maxError) {
    console.error("Error finding max org_id:", JSON.stringify(maxError, null, 2));
    return null;
  }

  // Calculate the new org_id
  const newOrgId = maxData.length > 0 ? maxData[0].org_id + 1 : 1;

  // Create a new org with the new org_id
  const { error: orgError } = await supabase
    .schema("nowadays")
    .from("orgs")
    .insert([{ org_id: newOrgId, org_name: `Org ${newOrgId}` }]);

  if (orgError) {
    console.error("Error creating new org:", JSON.stringify(orgError, null, 2));
    return null;
  }

  // Add user to the org
  const { error: userError } = await supabase
    .schema("nowadays")
    .from("org_users")
    .insert([{ org_id: newOrgId, user_id: user.user_id }]);

  if (userError) {
    console.error("Error adding user to org:", JSON.stringify(userError, null, 2));
    return null;
  }

  // Return the new org ID
  return { newOrgId, userId: user.user_id };
}

// Function to get all users in a specific org
export async function getUsersInOrg(
  supabase: SupabaseClient,
  orgId: number
): Promise<(Users & { user_type: string })[] | null> {
  const { data: orgUsers, error: orgUsersError } = await supabase
    .schema("nowadays")
    .from("org_users")
    .select("user_id")
    .eq("org_id", orgId);

  if (orgUsersError) {
    console.error("Error fetching users from org:", JSON.stringify(orgUsersError, null, 2));
    return null;
  }

  const userIds = orgUsers.map((user) => user.user_id);

  const { data: users, error: usersError } = await supabase
    .schema("nowadays")
    .from("users")
    .select("*")
    .in("user_id", userIds);

  if (usersError) {
    console.error("Error fetching user details:", JSON.stringify(usersError, null, 2));
    return null;
  }

  // Map users with default user_type
  const usersWithType = users.map((user) => ({
    ...user,
    user_type: "attendee", // Default user type for org users
  }));

  return usersWithType;
}

/**
 * EVENT MANAGEMENT - VENUE INTERACTION FUNCTIONS
 *
 * these operations will change the database.
 *
 */

// this function in particular throws errors if something goes wrong
export async function loveOrDeleteEventVenue(
  supabase: SupabaseClient,
  event: Events,
  venue: VenueOption,
  marked_as_liked?: boolean,
  sendRFPImmediately: boolean = true
): Promise<void> {
  const user = await getCurrentUser(supabase);

  const updatedEventHotelCandidate: Partial<EventVenueCandidate> = {
    marked_as_liked: marked_as_liked ?? venue.candidate.marked_as_liked,
    marked_as_liked_user_id: marked_as_liked ? user?.user_id : undefined,
  };
  const { error } = await supabase
    .schema("nowadays")
    .from("event_venue_candidate")
    .update(updatedEventHotelCandidate)
    .eq("event_venue_candidate_id", venue.candidate.event_venue_candidate_id);
  if (error) {
    console.error("Error updating hotel candidate: ", JSON.stringify(error, null, 2));
    throw error;
  }

  const [{ data: hotelData, error: hotelError }] = await Promise.all([
    supabase
      .schema("nowadays")
      .from("venue_static")
      .select("venue_name, city")
      .eq("venue_static_id", venue.candidate.venue_static_id),
  ]);
  if (hotelError || !hotelData || hotelData.length === 0) {
    console.error("Error fetching hotel data:", JSON.stringify(hotelError, null, 2));
    throw hotelError;
  }

  const venueName = hotelData[0].venue_name;
  const venueCity = hotelData[0].city;

  if (user) {
    sendSlackMessage(
      SlackNotificationType.EVENT,
      marked_as_liked ? "EVENT VENUE LIKED" : "REMOVED EVENT VENUE LIKE",
      `${user.first_name} ${user.last_name} (${user.email}) ${marked_as_liked ? "liked a venue" : "removed like from a venue"}`,
      [
        {
          title: "Event ID",
          message: venue.candidate.event_id.toString(),
        },
        {
          title: "Candidate ID",
          message: venue.candidate.event_venue_candidate_id.toString(),
        },
        {
          title: "Venue Static ID",
          message: venue.candidate.venue_static_id.toString(),
        },
        { title: "Venue Name", message: venueName },
        { title: "City", message: venueCity },
      ]
    );
  }

  if (marked_as_liked && sendRFPImmediately) {
    const eventOwner = await getEventOwner(supabase, event);
    sendRFPEmailsAutomatically(supabase, event, eventOwner, [venue]);
  }
}

export async function updateCustomerDecisionStatus(
  supabase: SupabaseClient,
  event_venue_candidate: EventVenueCandidate,
  decision_status: string,
  decision_status_reason?: string
): Promise<void> {
  const updatedEventHotelCandidate: Partial<EventVenueCandidate> = {
    customer_decision_status: decision_status,
    customer_decision_status_reason: decision_status_reason,
  };
  const { error } = await supabase
    .schema("nowadays")
    .from("event_venue_candidate")
    .update(updatedEventHotelCandidate)
    .eq("event_venue_candidate_id", event_venue_candidate.event_venue_candidate_id);

  if (error) {
    console.error("Error updating customer decision status:", JSON.stringify(error, null, 2));
    throw error;
  }

  // Slack notification is best-effort — the decision is already persisted above, so a
  // Slack outage must not surface as a failed operation to the user.
  try {
    const user = await getCurrentUser(supabase);
    const venue = await getStaticVenue(supabase, event_venue_candidate.venue_static_id);
    if (user) {
      const slackPrefix = await getEventPocSlackMentionAction(event_venue_candidate.event_id);
      // Escape user-controlled values (client name/email, venue name, free-text reason) so a
      // value like "<!channel>" can't inject a Slack mention. slackPrefix is trusted.
      const actorLabel = escapeSlackText(`${user.first_name} ${user.last_name} (${user.email})`);
      await sendSlackMessage(
        SlackNotificationType.EVENT,
        `CUSTOMER DECISION: ${decision_status.toUpperCase()}`,
        `${slackPrefix} ${actorLabel} updated the customer decision status for a venue`,
        [
          {
            title: "Event ID",
            message: event_venue_candidate.event_id.toString(),
          },
          {
            title: "Candidate ID",
            message: event_venue_candidate.event_venue_candidate_id.toString(),
          },
          {
            title: "Venue Static ID",
            message: event_venue_candidate.venue_static_id.toString(),
          },
          {
            title: "Venue Name",
            message: escapeSlackText(venue?.venue_name || "Unknown"),
          },
          { title: "New Decision Status", message: decision_status },
          {
            title: "Decision Status Reason",
            message: escapeSlackText(decision_status_reason || "No reason provided"),
          },
        ]
      );
    }
  } catch (notifyError) {
    console.error("updateCustomerDecisionStatus: failed to send Slack notification", notifyError);
  }
}

//sets show on dashbaord to false
export async function deselectHotelsFromEvent(
  supabase: SupabaseClient,
  event: Events,
  hotels: StaticVenue[],
  show_on_dashboard: boolean = false
): Promise<boolean> {
  const { data, error } = await supabase
    .schema("nowadays")
    .from("event_venue_candidate")
    .update({ show_on_dashboard: show_on_dashboard })
    .eq("event_id", event.event_id) // by default, filters are combined via AND
    .in(
      "venue_static_id",
      hotels.map((hotel) => hotel.venue_static_id)
    );
  if (error) {
    console.error("Error updating event:", JSON.stringify(error, null, 2));
    return false;
  }

  return true;
}

export async function customerRemoveVenueFromDashboard(
  supabase: SupabaseClient,
  event: Events,
  hotel: StaticVenue
): Promise<boolean> {
  const { data, error } = await supabase
    .schema("nowadays")
    .from("event_venue_candidate")
    .update({
      customer_removed: true,
      // archived: true
    })
    .eq("event_id", event.event_id)
    .eq("venue_static_id", hotel.venue_static_id);

  if (error) {
    console.error("Error updating event:", JSON.stringify(error, null, 2));
    return false;
  }

  const user = await getCurrentUser(supabase);

  if (user) {
    await sendSlackMessage(
      SlackNotificationType.EVENT,
      "HIDDEN VENUE FROM EVENT",
      `${user.first_name} ${user.last_name} (${user.email}) deleted a venue from their event`,
      [
        { title: "Event ID", message: event.event_id.toString() },
        {
          title: "Venue Static ID",
          message: hotel.venue_static_id.toString(),
        },
        { title: "Venue Name", message: hotel.venue_name },
      ]
    );
  }

  return true;
}

// Leave an additional comment on a venue location
export async function additionalCommentEventVenue(
  supabase: SupabaseClient,
  event_venue_candidate: EventVenueCandidate,
  additional_comment: string
): Promise<void> {
  const updatedEventHotelCandidate: Partial<EventVenueCandidate> = {
    additional_comments: additional_comment,
  };

  const { error } = await supabase
    .schema("nowadays")
    .from("event_venue_candidate")
    .update(updatedEventHotelCandidate)
    .eq("event_venue_candidate_id", event_venue_candidate.event_venue_candidate_id);

  if (error) {
    console.error("Error leaving feedback:", JSON.stringify(error, null, 2));
    throw error;
  }

  // Get the venue name
  const { data: hotelData, error: hotelError } = await supabase
    .schema("nowadays")
    .from("venue_static")
    .select("venue_name, city")
    .eq("venue_static_id", event_venue_candidate.venue_static_id);

  if (hotelError || !hotelData || hotelData.length === 0) {
    console.error("Error fetching hotel data:", JSON.stringify(hotelError, null, 2));
    throw hotelError;
  }

  const venueName = hotelData[0].venue_name;
  const venueCity = hotelData[0].city;

  const user = await getCurrentUser(supabase);
  if (user) {
    await sendSlackMessage(
      SlackNotificationType.EVENT,
      "EVENT VENUE ADDITIONAL COMMENT",
      `${user.first_name} ${user.last_name} (${user.email}) left an additional comment on a venue`,
      [
        {
          title: "Event ID",
          message: event_venue_candidate.event_id.toString(),
        },
        {
          title: "Candidate ID",
          message: event_venue_candidate.event_venue_candidate_id.toString(),
        },
        { title: "Additional Comment", message: additional_comment },
        {
          title: "Venue Static ID",
          message: event_venue_candidate.venue_static_id.toString(),
        },
        { title: "Venue Name", message: venueName },
        { title: "City", message: venueCity },
      ]
    );
  }
}

/**
 * Add transit to origin details to a venue.
 */
export async function addTransitToEventVenue(
  supabase: SupabaseClient,
  event_venue_candidate: EventVenueCandidate,
  distance_from_origin_in_minutes: number,
  mode_of_travel_from_origin: string,
  custom_origin_name: string
): Promise<void> {
  const updatedEventVenueCandidate: Partial<EventVenueCandidate> = {
    distance_from_origin_in_minutes: distance_from_origin_in_minutes,
    mode_of_travel_from_origin: mode_of_travel_from_origin,
    custom_origin_name: custom_origin_name,
  };

  const { error } = await supabase
    .schema("nowadays")
    .from("event_venue_candidate")
    .update(updatedEventVenueCandidate)
    .eq("event_venue_candidate_id", event_venue_candidate.event_venue_candidate_id);

  if (error) {
    console.error("Error updating transit details:", JSON.stringify(error, null, 2));
    throw error;
  }
}

export async function updateEventHotelChainRepEmailBody(
  supabase: SupabaseClient,
  eventId: number,
  emailBody: string
): Promise<boolean> {
  const { error } = await supabase
    .schema("nowadays")
    .from("events")
    .update({ chain_rep_email_body: emailBody })
    .eq("event_id", eventId);

  if (error) {
    console.error("Error updating last hotel chain rep email body:", error);
    return false;
  }

  return true;
}

export async function updateHotelChainRep(
  supabase: SupabaseClient,
  rep: {
    first_name: string;
    last_name: string;
    email: string;
    description: string;
    chain_name: string | null;
    hotel_rep_id: number;
    preferred_rep: boolean;
  }
): Promise<boolean> {
  // If chain_name is provided, look up chain_id from venue_chains table
  let chain_id: number | null = null;
  if (rep.chain_name) {
    const { data: chainData, error: chainError } = await supabase
      .schema("nowadays")
      .from("venue_chains")
      .select("chain_id")
      .eq("chain_name", rep.chain_name)
      .single();

    if (chainError || !chainData) {
      console.error("Error finding chain_id for chain_name:", rep.chain_name, chainError);
      return false;
    }
    chain_id = chainData.chain_id;
  }

  const { error } = await supabase
    .schema("nowadays")
    .from("hotel_chain_reps")
    .update({
      first_name: rep.first_name,
      last_name: rep.last_name,
      email: rep.email,
      description: rep.description,
      chain_id: chain_id,
      chain: rep.chain_name || null,
      preferred_rep: rep.preferred_rep,
    })
    .eq("hotel_rep_id", rep.hotel_rep_id);

  if (error) {
    console.error("Error updating hotel chain rep:", error);
    return false;
  }

  return true;
}

export async function deleteHotelChainRep(supabase: SupabaseClient, hotel_rep_id: number): Promise<boolean> {
  const { error } = await supabase
    .schema("nowadays")
    .from("hotel_chain_reps")
    .delete()
    .eq("hotel_rep_id", hotel_rep_id);

  if (error) {
    console.error("Error deleting hotel chain rep:", error);
    return false;
  }

  return true;
}

export async function getHotelChainRepsByOrgId(
  supabase: SupabaseClient,
  orgId: number
): Promise<
  | {
      first_name: string | null;
      last_name: string | null;
      email: string;
      description: string | null;
      chain_id: number | null;
      hotel_rep_id: number;
      preferred_rep: boolean;
      direct_recipient: boolean;
      chain_name: string | null;
      org_id: number | null;
    }[]
  | null
> {
  const { data, error } = await supabase
    .schema("nowadays")
    .from("hotel_chain_reps")
    .select(
      `
      first_name,
      last_name,
      email,
      description,
      chain_id,
      hotel_rep_id,
      preferred_rep,
      direct_recipient,
      org_id,
      venue_chains:chain_id (
        chain_name
      )
    `
    )
    .eq("org_id", orgId);

  if (error) {
    console.error("Error fetching hotel chain reps by org_id:", error);
    return null;
  }

  return (
    data?.map((rep: any) => ({
      first_name: rep.first_name,
      last_name: rep.last_name,
      email: rep.email,
      description: rep.description,
      chain_id: rep.chain_id,
      hotel_rep_id: rep.hotel_rep_id,
      preferred_rep: rep.preferred_rep,
      direct_recipient: rep.direct_recipient,
      org_id: rep.org_id,
      chain_name: rep.venue_chains?.chain_name || null,
    })) || null
  );
}

export async function createHotelChainRep(
  supabase: SupabaseClient,
  rep: {
    first_name: string;
    last_name: string;
    email: string;
    description: string;
    chain_id: number | null;
    org_id: number;
    preferred_rep: boolean;
  }
): Promise<number | null> {
  const { data, error } = await supabase
    .schema("nowadays")
    .from("hotel_chain_reps")
    .insert({
      first_name: rep.first_name,
      last_name: rep.last_name,
      email: rep.email,
      description: rep.description,
      chain_id: rep.chain_id,
      org_id: rep.org_id,
      preferred_rep: rep.preferred_rep,
    })
    .select("hotel_rep_id")
    .single();

  if (error) {
    console.error("Error creating hotel chain rep:", error);
    return null;
  }

  return data?.hotel_rep_id || null;
}

/**
 * Creates a form and seeds its default email settings.
 *
 * New forms default their email reply-to to the form owner's address, so replies to the form's
 * emails reach whoever created it. This is written into `email_templates.shared_settings` at
 * creation only — existing forms are never touched — and remains editable in the email builder.
 * When the owner's email can't be resolved, the form is created without a seeded reply-to (send
 * then falls back to the per-event `events+<event_id>@nowadays.ai` alias).
 *
 * @param supabase - Supabase client.
 * @param eventId - Event the form belongs to.
 * @param ownerUserId - User creating/owning the form; their email becomes the default reply-to.
 * @param formFields - Initial form fields.
 * @param formName - Form name.
 * @param formDescription - Form description.
 * @param isPublic - Whether the form is publicly accessible.
 * @returns The new form's id, or `null` on failure.
 */
export async function createForm(
  supabase: SupabaseClient,
  eventId: number,
  ownerUserId: number,
  formFields: FormField[],
  formName: string,
  formDescription: string,
  isPublic: boolean
): Promise<number | null> {
  // Seed the reply-to default from the owner's email (see JSDoc). Best-effort: a failed lookup
  // just omits the seed rather than blocking form creation.
  const owner = await getUserFromId(supabase, ownerUserId);
  const ownerEmail = owner?.email?.trim();
  const emailTemplates =
    ownerEmail && ownerEmail.includes("@") ? { shared_settings: { replyTo: ownerEmail } } : undefined;

  const { data, error } = await supabase
    .schema("nowadays")
    .from("forms")
    .insert([
      {
        event_id: eventId,
        owner_user_id: ownerUserId,
        form_fields: formFields,
        name: formName,
        description: formDescription,
        is_public: isPublic,
        ...(emailTemplates ? { email_templates: emailTemplates } : {}),
      },
    ])
    .select("form_id")
    .single();

  if (error) {
    console.error("Error creating form:", error);
    return null;
  }

  return data?.form_id || null;
}

export async function getFormsByEventId(supabase: SupabaseClient, eventId: number): Promise<Form[] | null> {
  const { data, error } = await supabase
    .schema("nowadays")
    .from("forms")
    .select("*")
    .eq("event_id", eventId)
    .order("created_at", { ascending: false });

  if (error) {
    console.error("Error fetching forms:", error);
    return null;
  }

  return data || [];
}

export async function getFormFields(supabase: SupabaseClient, formId: number): Promise<Form | null> {
  const { data, error } = await supabase.schema("nowadays").from("forms").select("*").eq("form_id", formId).single();

  if (error) {
    console.error("Error fetching form fields:", error);
    return null;
  }

  return data;
}

export async function updateFormFields(
  supabase: SupabaseClient,
  formId: number,
  formFields: FormField[],
  formName: string,
  formDescription: string,
  customLogoPath?: string,
  isPublic?: boolean
): Promise<boolean> {
  try {
    const updates = {
      form_fields: formFields,
      name: formName,
      description: formDescription,
      ...(customLogoPath && { custom_logo_path: customLogoPath }),
      ...(isPublic !== undefined && { is_public: isPublic }),
    };

    const { data, error } = await supabase
      .schema("nowadays")
      .from("forms")
      .update(updates)
      .eq("form_id", formId)
      .maybeSingle();

    if (error) {
      console.error("Error updating form fields:", error);
      return false;
    }

    return true;
  } catch (error) {
    console.error("Unexpected error updating form fields:", error);
    return false;
  }
}

/**
 * Reads a signed-in user's response to a form: the row stamped with their user id, else an unowned
 * row whose `response_data.Email` is their email. Read only: it never claims (writes `user_id` onto)
 * a row; ownership is claimed only by save-response, on a submit with proof.
 *
 * @param supabase - Service client (the caller has already verified the session)
 * @param formId - The form
 * @param userId - The caller's own user id, taken from the verified session, never from the request
 * @param userEmail - The caller's own verified email
 * @returns The response data, or null when there is none or a read fails
 * @throws Never
 */
export async function getUserFormResponse(
  supabase: SupabaseClient,
  formId: number,
  userId: number,
  userEmail: string
): Promise<{ [key: string]: any } | null> {
  // First attempt to get the response by userId
  const { data, error } = await supabase
    .schema("nowadays")
    .from("form_responses")
    .select("response_data")
    .eq("form_id", formId)
    .eq("user_id", userId)
    .maybeSingle();

  if (error) {
    console.error("Error fetching user form response by userId:", error);
    return null;
  }

  if (data) return data.response_data || null;

  // Otherwise an unowned row whose response_data Email matches. Matched exactly below; the ilike
  // only narrows the read.
  const { data: emailData, error: emailError } = await supabase
    .schema("nowadays")
    .from("form_responses")
    .select("response_data")
    .eq("form_id", formId)
    .is("user_id", null)
    .ilike("response_data->>Email", `%${userEmail}%`);

  if (emailError) {
    console.error("Error fetching user form response by email:", emailError);
    return null;
  }

  const match = (emailData || []).find((response) => response.response_data?.Email === userEmail);
  return match?.response_data || null;
}

/**
 * Retrieves all responses for a specific form.
 *
 * @param supabase The SupabaseClient instance.
 * @param formId The ID of the form for which to retrieve responses.
 * @returns An array of form responses or null if an error occurs.
 */
export async function getAllFormResponses(
  supabase: SupabaseClient,
  formId: number
): Promise<{ [key: string]: any }[] | null> {
  const { data, error } = await supabase
    .schema("nowadays")
    .from("form_responses")
    .select("response_id, response_data, user_id, last_updated, status, email, created_at, submitted_at, attended")
    .eq("form_id", formId);

  if (error) {
    console.error("Error fetching form responses:", error);
    return null;
  }

  return data.map((response) => ({
    ...response.response_data,
    response_id: response.response_id,
    user_id: response.user_id,
    last_updated: response.last_updated,
    status: response.status,
    email: response.email || response.response_data?.Email || response.response_data?.email,
    created_at: response.created_at,
    submitted_at: response.submitted_at,
    attended: response.attended === true,
  }));
}

const FORM_UPLOAD_EXTENSIONS: Record<string, string> = {
  "image/png": "png",
  "image/jpeg": "jpg",
  "image/webp": "webp",
  "image/gif": "gif",
  "application/pdf": "pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "docx",
  "application/msword": "doc",
};

const FORM_UPLOAD_MAX_SIZE_BYTES = 10 * 1024 * 1024;

/**
 * Uploads a form response file (signature PNG, document, etc.) to the forms bucket.
 *
 * Filename is generated server-side from a UUID + an extension derived from the
 * MIME allowlist — the client-provided `file.name` is intentionally discarded
 * to prevent path traversal and storage-path injection.
 *
 * @param supabase - Supabase client
 * @param formId - Form ID (used as top-level storage folder)
 * @param userId - Authenticated user ID, or null for anonymous responders
 * @param questionId - Question/field ID under which to nest the upload
 * @param file - File from a browser File input or canvas blob
 * @returns Storage path with leading slash, or null on failure
 */
export async function uploadFormFile(
  supabase: SupabaseClient,
  formId: number,
  userId: number | null,
  questionId: string,
  file: File
): Promise<string | null> {
  try {
    if (!Number.isInteger(formId) || formId < 1) {
      console.error("uploadFormFile: invalid formId", { formId });
      return null;
    }

    if (file.size > FORM_UPLOAD_MAX_SIZE_BYTES) {
      console.error("uploadFormFile: file too large", { size: file.size });
      return null;
    }

    const extension = FORM_UPLOAD_EXTENSIONS[file.type];
    if (!extension) {
      console.error("uploadFormFile: disallowed mime type", { mime: file.type });
      return null;
    }

    // questionId is sanitised to a fixed character set so it cannot escape the
    // form's storage prefix even if a malicious form definition were submitted.
    const safeQuestionId = questionId.replace(/[^a-zA-Z0-9_-]/g, "_").slice(0, 64) || "field";

    const bucketName = "forms";
    const filePrefix = userId ? `user-${userId}` : `anon-${crypto.randomUUID()}`;
    const filename = `${filePrefix}-${Date.now()}-${crypto.randomUUID()}.${extension}`;
    const filePath = `${formId}/responses/${safeQuestionId}/${filename}`;

    if (userId) {
      const { data: existingFiles } = await supabase.storage
        .from(bucketName)
        .list(`${formId}/responses/${safeQuestionId}`, {
          search: `user-${userId}`,
        });

      if (existingFiles && existingFiles.length > 0) {
        await supabase.storage
          .from(bucketName)
          .remove(existingFiles.map((existing) => `${formId}/responses/${safeQuestionId}/${existing.name}`));
      }
    }

    const { data, error } = await supabase.storage.from(bucketName).upload(filePath, file, { contentType: file.type });

    if (error) {
      console.error("uploadFormFile: upload failed", error);
      return null;
    }

    return data?.path ? `/${data.path}` : null;
  } catch (error) {
    console.error("uploadFormFile: unexpected error", error);
    return null;
  }
}

export function getFormFileUrl(supabase: SupabaseClient, filePath: string): string {
  // Remove leading slash if present since getPublicUrl doesn't expect it
  const cleanPath = filePath.startsWith("/") ? filePath.slice(1) : filePath;
  const { data } = supabase.storage.from("forms").getPublicUrl(cleanPath);

  return data.publicUrl;
}

export async function updateFormResponseStatusById(
  supabase: SupabaseClient,
  responseId: number,
  status: "approved" | "rejected" | "submitted" | null
): Promise<{ success: boolean; error?: string }> {
  try {
    const { error: updateError } = await supabase
      .schema("nowadays")
      .from("form_responses")
      // Null sheets_synced_at so the new status is treated as needing a sheet write; the
      // caller's re-sync stamps it back on success, and the resync cron retries on failure.
      .update({ status: status as any, sheets_synced_at: null })
      .eq("response_id", responseId);

    if (updateError) {
      console.error("Error updating response status:", updateError);
      return { success: false, error: updateError.message };
    }

    return { success: true };
  } catch (error) {
    console.error("Error in updateFormResponseStatusById:", error);
    return { success: false, error: String(error) };
  }
}

/**
 * Batched form of {@link updateFormResponseStatusById} — writes the same status to every
 * response id in one statement instead of one round trip per id, for bulk approve/reject.
 *
 * @param supabase - Supabase client
 * @param responseIds - Response ids to update
 * @param status - The status to write
 * @returns Success, or an error string
 */
export async function updateFormResponseStatusByIds(
  supabase: SupabaseClient,
  responseIds: number[],
  status: "approved" | "rejected" | "submitted" | null
): Promise<{ success: boolean; error?: string }> {
  if (responseIds.length === 0) return { success: true };

  try {
    const { error: updateError } = await supabase
      .schema("nowadays")
      .from("form_responses")
      .update({ status: status as any, sheets_synced_at: null })
      .in("response_id", responseIds);

    if (updateError) {
      console.error("Error updating response statuses:", updateError);
      return { success: false, error: updateError.message };
    }

    return { success: true };
  } catch (error) {
    console.error("Error in updateFormResponseStatusByIds:", error);
    return { success: false, error: String(error) };
  }
}

export async function deleteFormResponses(
  supabase: SupabaseClient,
  responseIds: number[]
): Promise<{ success: boolean; error?: string; deletedCount?: number }> {
  try {
    if (!responseIds || responseIds.length === 0) {
      return { success: false, error: "No response IDs provided" };
    }

    const { error: deleteError } = await supabase
      .schema("nowadays")
      .from("form_responses")
      .delete()
      .in("response_id", responseIds);

    if (deleteError) {
      console.error("Error deleting responses:", deleteError);
      return { success: false, error: deleteError.message };
    }

    return { success: true, deletedCount: responseIds.length };
  } catch (error) {
    console.error("Error in deleteFormResponses:", error);
    return { success: false, error: String(error) };
  }
}

export async function uploadLogo(supabase: SupabaseClient, file: File, formId: number): Promise<string | null> {
  try {
    const bucketName = "forms";
    const filePath = `${formId}/${file.name}`;

    // Upload with upsert to allow overwriting existing files
    const { data, error } = await supabase.storage.from(bucketName).upload(filePath, file, { upsert: true });

    if (error) {
      console.error("Error uploading logo:", error);
      return null;
    }

    // Ensure the path starts with a leading slash
    return data?.path ? `/${data.path}` : null;
  } catch (error) {
    console.error("Unexpected error uploading logo:", error);
    return null;
  }
}

export async function getImageUrl(supabase: SupabaseClient, path: string): Promise<string> {
  // Handle public assets
  if (path === "/nowadays_black.svg") {
    return path;
  }

  // Remove leading slash if present
  const cleanPath = path.startsWith("/") ? path.slice(1) : path;
  const bucketName = "forms";

  // If the path contains 'responses/', use signed URLs
  if (cleanPath.includes("responses/")) {
    try {
      const { data, error } = await supabase.storage.from(bucketName).createSignedUrl(cleanPath, 3600); // URL valid for 1 hour

      if (error) {
        console.error("Error creating signed URL:", error);
        return "";
      }
      return data.signedUrl;
    } catch (error) {
      console.error("Error in getImageUrl:", error);
      return "";
    }
  }

  // For all other files, use public URL
  const { data } = supabase.storage.from(bucketName).getPublicUrl(cleanPath);
  return data.publicUrl;
}

/**
 * Searches for venues near the specified location using the get_nearby_venues database function.
 *
 * `get_nearby_venues` has no limit argument of its own, so the row cap is applied to its raw
 * result immediately, before any chain/brand lookup or candidate join runs. That downstream work
 * scales with the capped set, not with every venue the radius search found. Pass `limit <= -1` to
 * opt out of the cap and get every match (used by callers that need the full unbounded list).
 *
 * @param supabase The Supabase client
 * @param event The current event (used to check candidate status)
 * @param location The selected location to search near
 * @param maxDistanceKm The maximum distance (in km) to search for venues
 * @param limit The maximum number of venues to process and return; pass -1 for no cap
 * @returns Array of venues with their candidate status, or null if error
 */
export async function searchVenuesByLocation(
  supabase: SupabaseClient,
  event: Events,
  location: { lat: number; lon: number; display_name: string },
  maxDistanceKm: number = 50, // Default search radius
  limit: number = 100
): Promise<
  | {
      hotel: StaticVenue;
      candidate: EventVenueCandidate;
    }[]
  | null
> {
  try {
    // Call the get_nearby_venues function with the provided location
    const { data: venueData, error } = await supabase.schema("nowadays").rpc("get_nearby_venues", {
      ref_latitude: location.lat,
      ref_longitude: location.lon,
      max_distance_km: maxDistanceKm,
    });

    if (error) {
      console.error("Error fetching nearby venues:", error);
      return null;
    }

    // Cap the raw RPC rows before any per-venue work. limit <= -1 is a deliberate "no cap"
    // sentinel some callers rely on to get the full unbounded result (see JSDoc above).
    const cappedVenueData: StaticVenue[] = limit > -1 ? venueData.slice(0, limit) : venueData;

    // Extract unique chain_ids and brand_ids, from the capped set only
    const chainIds = [...new Set(cappedVenueData.map((v: StaticVenue) => v.chain_id).filter(Boolean))];
    const brandIds = [...new Set(cappedVenueData.map((v: StaticVenue) => v.brand_id).filter(Boolean))];

    // Fetch chain and brand names
    const [chains, brands] = await Promise.all([
      chainIds.length
        ? supabase.schema("nowadays").from("venue_chains").select("chain_id, chain_name").in("chain_id", chainIds)
        : { data: [] },
      brandIds.length
        ? supabase.schema("nowadays").from("venue_chain_brands").select("brand_id, brand_name").in("brand_id", brandIds)
        : { data: [] },
    ]);

    const chainMap = new Map((chains.data || []).map((c) => [c.chain_id, c.chain_name]));
    const brandMap = new Map((brands.data || []).map((b) => [b.brand_id, b.brand_name]));

    // Get existing venue candidates for this event to mark which ones are already selected
    const { data: existingCandidates, error: candidateError } = await supabase
      .schema("nowadays")
      .from("event_venue_candidate")
      .select("*")
      .eq("event_id", event.event_id);

    if (candidateError) {
      console.error("Error fetching existing candidates:", candidateError);
      return null;
    }

    // Map the capped venues to include their candidate status
    return cappedVenueData.map((venue: StaticVenue) => ({
      hotel: {
        ...venue,
        chain_name: venue.chain_id ? chainMap.get(venue.chain_id) || undefined : undefined,
        brand_name: venue.brand_id ? brandMap.get(venue.brand_id) || undefined : undefined,
      },
      candidate: existingCandidates.find((c) => c.venue_static_id === venue.venue_static_id) || {
        event_id: event.event_id,
        venue_static_id: venue.venue_static_id,
        created_at: new Date().toISOString(),
      },
    }));
  } catch (error) {
    console.error("Error in searchVenuesByLocation:", error);
    return null;
  }
}

export async function saveTemplateDownload(
  supabase: SupabaseClient,
  email: string,
  templateTitle: string
): Promise<void> {
  const insertData = {
    email: email,
    origin: "templates",
    created_at: new Date().toISOString(),
    metadata: {
      template_title: templateTitle,
    },
  };

  const { data, error } = await supabase.schema("nowadays").from("landing_page_submissions").insert([insertData]);

  if (error) {
    console.error("❌ Error saving template download:", JSON.stringify(error, null, 2));
  }

  try {
    await notifyTemplateDownloaded({ email, templateTitle });
  } catch (slackError) {
    console.error("❌ Error sending Slack notification:", slackError);
  }
}

export async function budgetEstimatorEmail(supabase: SupabaseClient, email: string, metadata?: any): Promise<void> {
  const insertData = {
    email: email,
    origin: "budget_estimator",
    created_at: new Date().toISOString(),
    metadata: metadata,
  };

  const { error } = await supabase.schema("nowadays").from("landing_page_submissions").insert([insertData]);

  if (error) throw new Error(`Couldn't save the estimator lead: ${error.message}`);

  try {
    await notifyBudgetEstimatorEmail({ email });
  } catch (slackError) {
    console.error("❌ Error sending Slack notification:", slackError);
  }
}

/**
 * GAM Profiles API service for fetching Global Account Manager profiles
 */

// Fetch all published GAM profiles
export async function fetchGamProfiles(supabase: SupabaseClient): Promise<GamProfile[]> {
  const { data: profiles, error } = await supabase
    .schema("nowadays")
    .from("gam_profiles")
    .select("*")
    .order("created_at", { ascending: false });

  if (error) {
    console.error("Error fetching GAM profiles:", error);
    throw new Error(`Failed to fetch GAM profiles: ${error.message}`);
  }

  // Parse JSON fields
  const parsedProfiles: GamProfile[] =
    profiles?.map((profile: any) => ({
      ...profile,
      favorite_hotels:
        typeof profile.favorite_hotels === "string"
          ? JSON.parse(profile.favorite_hotels)
          : profile.favorite_hotels || [],
      travel_photos:
        typeof profile.travel_photos === "string" ? JSON.parse(profile.travel_photos) : profile.travel_photos || [],
    })) || [];

  return parsedProfiles;
}

// Event Venue Booking functions
export async function getEventVenueBooking(
  supabase: SupabaseClient,
  eventId: number,
  venueStaticId: number
): Promise<EventVenueBooking | null> {
  const { data, error } = await supabase
    .schema("nowadays")
    .from("event_venue_bookings")
    .select("*")
    .eq("event_id", eventId)
    .eq("venue_static_id", venueStaticId)
    .single();

  if (error) {
    // PGRST116 means no rows found, which is expected when booking doesn't exist yet
    if (error.code !== "PGRST116") {
      console.error("Error fetching event venue booking:", error);
    }
    return null;
  }

  return data;
}

export async function createOrUpdateEventVenueBooking(
  supabase: SupabaseClient,
  booking: Partial<EventVenueBooking>,
  event: Events
): Promise<EventVenueBooking | null> {
  // Auto-fill start_date and end_date from event if not provided
  if (!booking.start_date && event.event_dates && event.event_dates.length > 0) {
    const eventDate = event.event_dates[0];
    booking.start_date = eventDate.startDate || eventDate.date || "";
  }
  if (!booking.end_date && event.event_dates && event.event_dates.length > 0) {
    const eventDate = event.event_dates[0];
    booking.end_date = eventDate.endDate || eventDate.date || "";
  }

  // No auto-calculation of room nights - user enters manually since it can vary per day

  // Auto-calculate total guestroom contracted if nightly rate and total room nights are available
  if (booking.nightly_rate && booking.total_room_nights && !booking.total_guestroom_contracted) {
    booking.total_guestroom_contracted = booking.nightly_rate * booking.total_room_nights;
  }

  // Auto-calculate total contracted if guestroom and meeting room totals exist
  if (
    !booking.total_contracted_estimate &&
    (booking.total_guestroom_contracted || booking.total_meeting_room_contracted)
  ) {
    const guestroom = booking.total_guestroom_contracted || 0;
    const meetingRoom = booking.total_meeting_room_contracted || 0;
    if (guestroom > 0 || meetingRoom > 0) {
      booking.total_contracted_estimate = guestroom + meetingRoom;
    }
  }

  // Set default currency from event if not provided
  if (!booking.currency && event.currency) {
    booking.currency = event.currency;
  } else if (!booking.currency) {
    booking.currency = "USD"; // fallback default
  }

  // Auto-calculate commission if not provided but percentage and guestroom total exist
  if (!booking.total_estimated_commission && booking.commission_percentage && booking.total_guestroom_contracted) {
    booking.total_estimated_commission = booking.total_guestroom_contracted * (booking.commission_percentage / 100);
  }

  // Check if booking already exists
  const existingBooking = await getEventVenueBooking(supabase, booking.event_id!, booking.venue_static_id!);

  let result;
  if (existingBooking) {
    // Update existing booking
    const { data, error } = await supabase
      .schema("nowadays")
      .from("event_venue_bookings")
      .update({
        ...booking,
        updated_at: new Date().toISOString(),
      })
      .eq("booking_id", existingBooking.booking_id)
      .select()
      .single();

    if (error) {
      console.error("Error updating event venue booking:", error);
      return null;
    }
    result = data;
  } else {
    // Create new booking
    const { data, error } = await supabase
      .schema("nowadays")
      .from("event_venue_bookings")
      .insert({
        ...booking,
        status: booking.status || "draft",
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      })
      .select()
      .single();

    if (error) {
      console.error("Error creating event venue booking:", error);
      return null;
    }
    result = data;
  }

  return result;
}

// User Custom Concessions Functions
export async function getUserCustomConcessions(supabase: SupabaseClient, userId: number): Promise<string[]> {
  const { data, error } = await supabase
    .schema("nowadays")
    .from("user_custom_concessions")
    .select("concession_text")
    .eq("user_id", userId)
    .order("usage_count", { ascending: false });

  if (error) {
    console.error("Error fetching user custom concessions:", error);
    return [];
  }

  return data.map((row) => row.concession_text);
}

export async function addUserCustomConcession(
  supabase: SupabaseClient,
  userId: number,
  concessionText: string
): Promise<boolean> {
  // Check if concession already exists for this user
  const { data: existing } = await supabase
    .schema("nowadays")
    .from("user_custom_concessions")
    .select("id, usage_count")
    .eq("user_id", userId)
    .eq("concession_text", concessionText)
    .single();

  if (existing) {
    // Update usage count
    const { error } = await supabase
      .schema("nowadays")
      .from("user_custom_concessions")
      .update({
        usage_count: existing.usage_count + 1,
        last_used_at: new Date().toISOString(),
      })
      .eq("id", existing.id);

    if (error) {
      console.error("Error updating custom concession usage:", error);
      return false;
    }
  } else {
    // Create new concession
    const { error } = await supabase.schema("nowadays").from("user_custom_concessions").insert({
      user_id: userId,
      concession_text: concessionText,
      usage_count: 1,
      last_used_at: new Date().toISOString(),
    });

    if (error) {
      console.error("Error creating custom concession:", error);
      return false;
    }
  }

  return true;
}

export async function migrateEventConcessionsToUser(
  supabase: SupabaseClient,
  userId: number,
  eventId: number
): Promise<void> {
  // Get event concessions
  const { data: event } = await supabase
    .schema("nowadays")
    .from("events")
    .select("custom_concessions")
    .eq("event_id", eventId)
    .single();

  if (!event || !event.custom_concessions || event.custom_concessions.length === 0) {
    return;
  }

  // Add each concession to user's history
  for (const concession of event.custom_concessions) {
    await addUserCustomConcession(supabase, userId, concession);
  }
}

// User Custom Questions Functions
export interface UserCustomQuestion {
  id: number;
  user_id: number;
  custom_question: string;
  created_at: string;
}

export async function getUserCustomQuestions(supabase: SupabaseClient, userId: number): Promise<UserCustomQuestion[]> {
  try {
    const { data, error } = await supabase
      .schema("nowadays")
      .from("user_custom_questions")
      .select("*")
      .eq("user_id", userId);

    if (error) {
      console.error("Error fetching user custom questions:", error);
      return [];
    }

    return data || [];
  } catch (error) {
    console.error("Error in getUserCustomQuestions:", error);
    return [];
  }
}

export async function addUserCustomQuestion(
  supabase: SupabaseClient,
  userId: number,
  question: string
): Promise<boolean> {
  try {
    // Check if the question already exists for this user
    const { data: existing } = await supabase
      .schema("nowadays")
      .from("user_custom_questions")
      .select("*")
      .eq("user_id", userId)
      .eq("custom_question", question)
      .single();

    if (existing) {
      // Question already exists, no need to update
      return true;
    } else {
      // Insert new question
      const { error: insertError } = await supabase.schema("nowadays").from("user_custom_questions").insert({
        user_id: userId,
        custom_question: question,
      });

      if (insertError) {
        console.error("Error inserting user custom question:", insertError);
        return false;
      }
    }

    return true;
  } catch (error) {
    console.error("Error in addUserCustomQuestion:", error);
    return false;
  }
}

export async function migrateEventQuestionsToUser(
  supabase: SupabaseClient,
  userId: number,
  customQuestions: string[]
): Promise<boolean> {
  try {
    if (!customQuestions || customQuestions.length === 0) {
      return true;
    }

    // Add each question to the user's history
    const results = await Promise.all(
      customQuestions.map((question) => addUserCustomQuestion(supabase, userId, question))
    );

    return results.every((result) => result === true);
  } catch (error) {
    console.error("Error in migrateEventQuestionsToUser:", error);
    return false;
  }
}

// =====================================================
// Landing Pages Functions
// =====================================================

/**
 * Creates a new landing page
 */
export async function createLandingPage(
  supabase: SupabaseClient,
  eventId: number,
  ownerUserId: number,
  name: string,
  slug: string,
  pageComponents: LandingPageComponent[] = [],
  isPublic: "private" | "invited" | "public" = "private"
): Promise<number | null> {
  try {
    const trimmedSlug = slug.trim();
    const [hasV1Conflict, hasV2Conflict] = await Promise.all([
      hasLandingPageSlugConflict(supabase, trimmedSlug),
      hasEventLandingPageSlugConflict(supabase, trimmedSlug),
    ]);

    if (hasV1Conflict || hasV2Conflict) {
      throw new SlugConflictError();
    }

    const { data, error } = await supabase
      .schema("nowadays")
      .from("landing_pages")
      .insert([
        {
          event_id: eventId,
          owner_user_id: ownerUserId,
          name: name,
          slug: trimmedSlug,
          page_components: pageComponents,
          is_public: isPublic,
          primary_color: "#5261EB", // Default primary color
        },
      ])
      .select("landing_page_id")
      .single();

    if (error) {
      if (error.code === "23505" && error.message.includes("slug")) {
        throw new SlugConflictError();
      }
      console.error("Error creating landing page:", error);
      return null;
    }

    return data?.landing_page_id || null;
  } catch (error) {
    if (error instanceof SlugConflictError) {
      throw error;
    }
    console.error("Error creating landing page:", error);
    return null;
  }
}

/**
 * Gets all landing pages for an event
 */
export async function getLandingPagesByEventId(
  supabase: SupabaseClient,
  eventId: number
): Promise<LandingPage[] | null> {
  const { data, error } = await supabase
    .schema("nowadays")
    .from("landing_pages")
    .select("*")
    .eq("event_id", eventId)
    .order("created_at", { ascending: false });

  if (error) {
    console.error("Error fetching landing pages:", error);
    return null;
  }

  return data || [];
}

/**
 * Updates a landing page (including slug)
 */
export async function updateLandingPage(
  supabase: SupabaseClient,
  landingPageId: number,
  name: string,
  pageComponents: LandingPageComponent[],
  customLogoPath?: string,
  isPublic?: "private" | "invited" | "public",
  primaryColor?: string,
  slug?: string,
  redirectSlugs?: string[],
  password?: string | null, // null to remove password, undefined to keep existing
  subpages?: LandingPageSubpage[], // Subpages with their own components
  chatbotEnabled?: boolean,
  chatbotAdditionalContext?: string
): Promise<boolean> {
  try {
    // Fetch current landing page to compare capacities
    const { data: currentLandingPage } = await supabase
      .schema("nowadays")
      .from("landing_pages")
      .select("page_components, slug")
      .eq("landing_page_id", landingPageId)
      .single();

    const updates: any = {
      name: name,
      page_components: pageComponents,
      ...(customLogoPath && { custom_logo_path: customLogoPath }),
      ...(isPublic !== undefined && { is_public: isPublic }),
      ...(primaryColor && { primary_color: primaryColor }),
    };

    // Only include slug in update if it's provided (not undefined)
    if (slug !== undefined) {
      const trimmedSlug = slug.trim();
      const slugChanged = currentLandingPage ? trimmedSlug !== currentLandingPage.slug : true;

      if (slugChanged) {
        const [hasV1Conflict, hasV2Conflict] = await Promise.all([
          hasLandingPageSlugConflict(supabase, trimmedSlug, landingPageId),
          hasEventLandingPageSlugConflict(supabase, trimmedSlug),
        ]);

        if (hasV1Conflict || hasV2Conflict) {
          throw new SlugConflictError();
        }
      }

      updates.slug = trimmedSlug;
    }

    // Only include redirect_slugs in update if it's provided (not undefined)
    if (redirectSlugs !== undefined) {
      updates.redirect_slugs = redirectSlugs;
    }

    // Handle password field: null removes it, undefined keeps existing, string sets it
    if (password !== undefined) {
      updates.password = password;
    }

    // Handle subpages field
    if (subpages !== undefined) {
      updates.subpages = subpages;
    }

    // Handle chatbot fields
    // NOTE: These columns must exist in the database before uncommenting.
    // Run migration to add: chatbot_enabled BOOLEAN, chatbot_additional_context TEXT
    if (chatbotEnabled !== undefined) {
      updates.chatbot_enabled = chatbotEnabled;
    }
    if (chatbotAdditionalContext !== undefined) {
      updates.chatbot_additional_context = chatbotAdditionalContext;
    }

    const { error } = await supabase
      .schema("nowadays")
      .from("landing_pages")
      .update(updates)
      .eq("landing_page_id", landingPageId);

    if (error) {
      // Check for unique constraint violation on slug
      if (error.code === "23505" && error.message.includes("slug")) {
        throw new SlugConflictError();
      }
      console.error("Error updating landing page:", error);
      return false;
    }

    // After successful update, check for capacity increases and notify waitlist
    if (currentLandingPage) {
      try {
        const oldActivities =
          (currentLandingPage.page_components as any[])?.find((c: any) => c.type === "activities")?.config
            ?.activities || [];
        const newActivities =
          (pageComponents as any[])?.find((c: any) => c.type === "activities")?.config?.activities || [];

        // Find slots where capacity increased
        const slotsWithIncreasedCapacity: Array<{ activityId: string; timeSlotId: string }> = [];

        for (const newActivity of newActivities) {
          if (!newActivity.signupEnabled || !newActivity.timeSlots) continue;

          const oldActivity = oldActivities.find((a: any) => a.id === newActivity.id);
          if (!oldActivity) continue;

          for (const newSlot of newActivity.timeSlots) {
            const oldSlot = oldActivity.timeSlots?.find((s: any) => s.id === newSlot.id);
            if (oldSlot && newSlot.capacity > oldSlot.capacity) {
              slotsWithIncreasedCapacity.push({
                activityId: newActivity.id,
                timeSlotId: newSlot.id,
              });
            }
          }
        }

        // Trigger waitlist notifications for slots with increased capacity
        if (slotsWithIncreasedCapacity.length > 0) {
          const landingPageSlug = slug || currentLandingPage.slug;

          // Get current session for auth
          const {
            data: { session },
          } = await supabase.auth.getSession();

          // Call the check-openings API to notify waitlist users
          fetch("/api/landing-pages/activity-waitlist/check-openings", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${session?.access_token || ""}`,
            },
            body: JSON.stringify({ landing_page_id: landingPageId }),
          }).catch((err) => console.error("[Landing Page Save] Error triggering waitlist check:", err));
        }
      } catch (capacityCheckError) {
        console.error("[Landing Page Save] Error checking capacity changes:", capacityCheckError);
        // Don't fail the save if capacity check fails
      }
    }

    return true;
  } catch (error) {
    if (error instanceof SlugConflictError) {
      throw error;
    }
    console.error("Unexpected error updating landing page:", error);
    return false;
  }
}

/**
 * Deletes a landing page
 */
export async function deleteLandingPage(supabase: SupabaseClient, landingPageId: number): Promise<boolean> {
  const { error } = await supabase
    .schema("nowadays")
    .from("landing_pages")
    .delete()
    .eq("landing_page_id", landingPageId);

  if (error) {
    console.error("Error deleting landing page:", error);
    return false;
  }

  return true;
}

export async function duplicateLandingPage(supabase: SupabaseClient, landingPageId: number): Promise<number | null> {
  try {
    const user = await getCurrentUser(supabase);
    if (!user) return null;

    // Get the original landing page
    const { data: originalPage, error: fetchError } = await supabase
      .schema("nowadays")
      .from("landing_pages")
      .select("*")
      .eq("landing_page_id", landingPageId)
      .single();

    if (fetchError || !originalPage) {
      console.error("Error fetching original landing page:", fetchError);
      return null;
    }

    // Generate a unique slug by appending a timestamp and retrying if it collides across V1/V2.
    const timestamp = Date.now();
    for (let attempt = 0; attempt < 10; attempt++) {
      const newSlug = `${originalPage.slug}-copy-${timestamp}${attempt === 0 ? "" : `-${attempt}`}`;
      const [hasV1Conflict, hasV2Conflict] = await Promise.all([
        hasLandingPageSlugConflict(supabase, newSlug),
        hasEventLandingPageSlugConflict(supabase, newSlug),
      ]);

      if (hasV1Conflict || hasV2Conflict) {
        continue;
      }

      // Create a new landing page with the same components but as a draft
      const { data, error } = await supabase
        .schema("nowadays")
        .from("landing_pages")
        .insert([
          {
            event_id: originalPage.event_id,
            owner_user_id: user.user_id,
            name: `${originalPage.name} (Copy)`,
            slug: newSlug,
            page_components: originalPage.page_components,
            is_public: "private", // Start as draft/private
            custom_logo_path: originalPage.custom_logo_path,
            primary_color: originalPage.primary_color,
          },
        ])
        .select("landing_page_id")
        .single();

      if (error) {
        console.error("Error duplicating landing page:", error);
        return null;
      }

      return data?.landing_page_id || null;
    }

    console.error("Error duplicating landing page: unable to find an available slug");
    return null;
  } catch (error) {
    console.error("Error duplicating landing page:", error);
    return null;
  }
}

export async function uploadLandingPageImage(
  supabase: SupabaseClient,
  file: File,
  landingPageId: number,
  componentId: string
): Promise<string | null> {
  try {
    const bucketName = "landing-pages";
    const timestamp = Date.now();
    // Sanitize filename: replace spaces and special characters with dashes
    const sanitizedFileName = file.name.replace(/\s+/g, "-").replace(/[^a-zA-Z0-9._-]/g, "-");
    const filePath = `${landingPageId}/components/${componentId}/${timestamp}-${sanitizedFileName}`;

    const { error } = await supabase.storage.from(bucketName).upload(filePath, file);

    if (error) {
      console.error("Error uploading landing page image:", error);
      return null;
    }

    // Return the public URL
    const { data: urlData } = supabase.storage.from(bucketName).getPublicUrl(filePath);
    return urlData?.publicUrl || null;
  } catch (error) {
    console.error("Unexpected error uploading landing page image:", error);
    return null;
  }
}

export interface EventWithPreferredFlags {
  event_id: number;
  name: string;
}

// Cache for events with preferred flags to avoid repeated database calls
let eventsWithPreferredFlagsCache: EventWithPreferredFlags[] | null = null;

export async function getEventsWithPreferredFlags(supabase: SupabaseClient): Promise<EventWithPreferredFlags[]> {
  // Return cached value if available
  if (eventsWithPreferredFlagsCache !== null) {
    return eventsWithPreferredFlagsCache;
  }

  const { data, error } = await supabase
    .schema("nowadays")
    .from("events_with_preferred_flags_for_demos")
    .select("event_id, name");

  if (error) {
    console.error("Error fetching events with preferred flags:", error);
    return [];
  }

  eventsWithPreferredFlagsCache = data || [];
  return eventsWithPreferredFlagsCache;
}
